import type { LucideIcon } from 'lucide-react';

export const supportedLanguages = ['sr', 'es', 'en'] as const;

export type Language = (typeof supportedLanguages)[number];

export type LanguageDetails = {
  code: Language;
  name: string;
  shortLabel: string;
  htmlLang: string;
  locale: string;
  ogLocale: string;
};

export type TextCard = {
  title: string;
  description: string;
};

export type ValueTextCard = TextCard & {
  value: string;
};

export type InsightLinkText = {
  label: string;
  to: string;
};

export type InsightFilterOptionText = {
  label: string;
  value: string;
};

export type PlatformCapabilityText = {
  label: string;
  title: string;
  lead: string;
  description: string;
  outcomes: readonly string[];
  route: readonly string[];
};

export type InfrastructureCardText = {
  title: string;
  description: string;
};

export type ContactValidationCopy = {
  firstNameMin: string;
  firstNameMax: string;
  firstNamePattern: string;
  lastNameMin: string;
  lastNameMax: string;
  lastNamePattern: string;
  emailInvalid: string;
  emailMax: string;
  messageMin: string;
  messageMax: string;
};

export type TranslationDictionary = {
  meta: {
    title: string;
    description: string;
    keywords: string;
    ogDescription: string;
    imageAlt: string;
    twitterDescription: string;
  };
  common: {
    backToTop: string;
    close: string;
    dataSliv: string;
    soc2Compliant: string;
    month: string;
    year: string;
    total: string;
  };
  nav: {
    logoAlt: string;
    primaryAriaLabel: string;
    openMenu: string;
    closeMenu: string;
    platform: string;
    calculator: string;
    cases: string;
    insights: string;
    insightsDropdownAriaLabel: string;
    contact: string;
    demoShort: string;
    demoLong: string;
  };
  footer: {
    tagline: string;
  };
  language: {
    names: Record<Language, string>;
    selectorLabel: string;
  };
  theme: {
    light: string;
    dark: string;
    switchToLightMode: string;
    switchToDarkMode: string;
  };
  home: {
    hero: {
      badge: string;
      title: string;
      descriptionBeforeAccent: string;
      descriptionAccent: string;
      descriptionAfterAccent: string;
      startMapping: string;
      getDemo: string;
      lifecycleStages: {
        ingest: string;
        govern: string;
        operate: string;
        monitor: string;
      };
    };
    technology: {
      benefitsTitle: string;
      benefitsAriaLabel: string;
      technologies: readonly string[];
      environmentsTitle: string;
      environments: readonly string[];
    };
    platform: {
      badge: string;
      title: string;
      description: string;
      menuLabel: string;
      navAriaLabel: string;
      scrollToPrefix: string;
      layerLabel: string;
      privateRuntime: string;
      typicalPath: string;
      metrics: {
        layer: string;
        controls: string;
        pathStages: string;
      };
      stepPrefix: string;
      capabilities: readonly PlatformCapabilityText[];
    };
    inspira: {
      badge: string;
      company: string;
      title: string;
      description: string;
      savingsLabel: string;
      savingsValue: string;
      savingsDetail: string;
      proofPoints: readonly TextCard[];
      cta: string;
    };
    buildVsBuy: {
      badge: string;
      title: string;
      description: string;
      buildEyebrow: string;
      buildTitle: string;
      buyEyebrow: string;
      buyTitle: string;
      recommended: string;
      stats: readonly {
        value: string;
        label: string;
        detail: string;
      }[];
      buildCosts: readonly TextCard[];
      buyAdvantages: readonly TextCard[];
      ctaText: string;
      explorePlatform: string;
      estimateCost: string;
    };
    calculator: {
      badge: string;
      title: string;
      description: string;
      totalMonthlyCost: string;
      controls: {
        computeUnits: {
          label: string;
          suffix: string;
        };
        dataVolume: {
          label: string;
          suffix: string;
          detailAt: string;
        };
        monthlyQueries: {
          label: string;
          suffix: string;
          included: string;
        };
      };
      savingsVsCloud: string;
      premiumVsCloud: string;
      comparedTo: string;
      costBreakdown: string;
      customPlanQuestion: string;
      getInContact: string;
      queryUnit: string;
      chartAriaLabel: string;
      chartShortLabels: Record<string, string>;
      breakdownRows: {
        platformFee: string;
        computeUnitsCost: string;
        storageCost: string;
        monthlyPlatformTotal: string;
      };
      chart: {
        dataSlivCostGraph: string;
        cloudComparisonGraph: string;
        breakdownDescription: string;
        comparisonDescriptionPrefix: string;
        comparisonDescriptionSuffix: string;
        threeValues: string;
        queriesPlusStorage: string;
        compareCloud: string;
        dataSlivGraph: string;
      };
      source: {
        basedOnArticle: string;
        by: string;
        benchmarkUses: string;
        workload: string;
        reportedCostPrefix: string;
        reportedCostSuffix: string;
      };
      customComparison: {
        title: string;
        description: string;
      };
      computeUnitModel: string;
      computeUnitServices: readonly string[];
      unitsAbbreviation: string;
    };
    compliance: {
      eyebrow: string;
      title: string;
      description: string;
      listLabel: string;
      note: string;
      items: Record<'gdpr' | 'soc2' | 'iso27001' | 'hipaa' | 'ccpa', string>;
    };
    security: {
      badge: string;
      title: string;
      bookDemo: string;
      infrastructureCards: readonly InfrastructureCardText[];
    };
    contactSection: {
      title: string;
      description: string;
      signal: string;
      getInContact: string;
    };
  };
  insights: {
    heroBadge: string;
    title: string;
    description: string;
    latestLabel: string;
    loadingPosts: string;
    emptyFeedTitle: string;
    emptyFeedDescription: string;
    linksLabel: string;
    allInsights: string;
    quickLinks: readonly InsightLinkText[];
    filters: {
      title: string;
      description: string;
      typeLabel: string;
      serviceLabel: string;
      industryLabel: string;
      allTypes: string;
      allServices: string;
      allIndustries: string;
      reset: string;
      results: string;
      emptyTitle: string;
      emptyDescription: string;
      previous: string;
      next: string;
      page: string;
      of: string;
      typeOptions: readonly {
        label: string;
        value: 'blog' | 'news';
      }[];
      serviceOptions: readonly InsightFilterOptionText[];
      industryOptions: readonly InsightFilterOptionText[];
    };
    finalCta: {
      title: string;
      description: string;
      contact: string;
    };
  };
  blog: {
    backToInsights: string;
    breadcrumbHome: string;
    breadcrumbInsights: string;
    published: string;
    updated: string;
    author: string;
    readTime: string;
    service: string;
    industry: string;
    relatedTitle: string;
    relatedDescription: string;
    readArticle: string;
    articleType: string;
    loadingArticle: string;
    loadErrorTitle: string;
    loadErrorDescription: string;
    authorRoleFallback: string;
    tags: string;
    share: string;
    shareOnLinkedIn: string;
    shareOnX: string;
    shareByEmail: string;
    copyLink: string;
    linkCopied: string;
    continueReading: string;
    browseAllInsights: string;
    articleImage: string;
  };
  cases: {
    heroBadge: string;
    title: string;
    description: string;
    estimateCost: string;
    explorePlatform: string;
    heroSelectorPrefix: string;
    heroCategoryAriaLabel: string;
    heroCategories: readonly string[];
    heroPrimaryCta: string;
    calculateSavings: string;
    bookDemo: string;
    platformName: string;
    heroVisual: {
      sourceLabel: string;
      outcomeLabel: string;
      sources: readonly string[];
      platformSteps: readonly string[];
      outcomes: readonly string[];
    };
    outcomeOverview: {
      title: string;
      cards: readonly ValueTextCard[];
    };
    featured: {
      selectorLabel: string;
      selectorAriaLabel: string;
      eyebrow: string;
      proofPoint: string;
      cta: string;
    };
    workflowTitle: string;
    workflowDescription: string;
    workflowSteps: readonly string[];
    sectionBadge: string;
    sectionTitle: string;
    patternBadge: string;
    patternTitle: string;
    patternDescription: string;
    capabilityBadge: string;
    capabilities: readonly {
      eyebrow: string;
      title: string;
      description: string;
      cta: string;
      metrics: readonly string[];
    }[];
    carousel: {
      title: string;
      description: string;
      previous: string;
      next: string;
      selectorAriaLabel: string;
      cards: readonly TextCard[];
    };
    finalCta: {
      title: string;
      description: string;
    };
    caseCards: readonly {
      eyebrow: string;
      title: string;
      summary: string;
      outcomes: readonly string[];
    }[];
    operatingPatterns: readonly TextCard[];
  };
  contact: {
    badge: string;
    title: string;
    description: string;
    supportPoints: readonly string[];
    explorePlatform: string;
    formTitle: string;
    mailerWarningBeforeCode: string;
    mailerWarningAfterCode: string;
    labels: {
      company: string;
      firstName: string;
      lastName: string;
      email: string;
      message: string;
    };
    sendMessage: string;
    validation: ContactValidationCopy;
    notifications: {
      received: string;
      mailerMissing: string;
      sent: string;
      failed: string;
    };
  };
  notFound: {
    routeSignal: string;
    live: string;
    connectionLost: string;
    checkingRoute: string;
    pageBadge: string;
    routeCheckBadge: string;
    title: string;
    description: string;
    goHome: string;
    goBack: string;
    requestedRoute: string;
    explorePlatformTitle: string;
    explorePlatformDescription: string;
    viewCasesTitle: string;
    viewCasesDescription: string;
    contactTeamTitle: string;
    contactTeamDescription: string;
  };
  errorBoundary: {
    title: string;
    description: string;
    detailsSummary: string;
    tryAgain: string;
    reloadPage: string;
    goHome: string;
  };
};

export type TranslatedIconCard = InfrastructureCardText & {
  icon: LucideIcon;
};

export const languageDetails: Record<Language, LanguageDetails> = {
  sr: {
    code: 'sr',
    name: 'Srpski',
    shortLabel: 'SR',
    htmlLang: 'sr-Latn-RS',
    locale: 'sr-Latn-RS',
    ogLocale: 'sr_RS'
  },
  es: {
    code: 'es',
    name: 'Español',
    shortLabel: 'ES',
    htmlLang: 'es-ES',
    locale: 'es-ES',
    ogLocale: 'es_ES'
  },
  en: {
    code: 'en',
    name: 'English',
    shortLabel: 'EN',
    htmlLang: 'en-US',
    locale: 'en-US',
    ogLocale: 'en_US'
  }
};

export const defaultLanguage: Language = 'en';

export const translations: Record<Language, TranslationDictionary> = {
  sr: {
    meta: {
      title: 'DataSliv - on-prem rešenje za podatke',
      description:
        'DataSliv je on-prem rešenje za privatni unos podataka, upravljanje, automatizaciju tokova rada i izveštavanje unutar vaše infrastrukture.',
      keywords:
        'DataSliv, on-prem podaci, privatna data platforma, upravljanje podacima, automatizacija tokova rada, data operacije',
      ogDescription:
        'Privatni kontrolni sloj za unos podataka, upravljanje, automatizaciju i izveštavanje unutar vaše infrastrukture.',
      imageAlt: 'DataSliv logo',
      twitterDescription:
        'Privatni kontrolni sloj za on-prem tokove rada sa podacima.'
    },
    common: {
      backToTop: 'Nazad na vrh',
      close: 'Zatvori',
      dataSliv: 'DataSliv',
      soc2Compliant: 'SOC 2 Compliant',
      month: 'mesec',
      year: 'godina',
      total: 'Ukupno'
    },
    nav: {
      logoAlt: 'DataSliv logo',
      primaryAriaLabel: 'Glavna navigacija',
      openMenu: 'Otvori meni',
      closeMenu: 'Zatvori meni',
      platform: 'Platforma',
      calculator: 'Kalkulator',
      cases: 'Primeri',
      insights: 'Insights',
      insightsDropdownAriaLabel: 'Insights meni',
      contact: 'Kontakt',
      demoShort: 'Demo',
      demoLong: 'Zakažite demo'
    },
    footer: {
      tagline: 'DataSliv - on-prem operacije nad podacima'
    },
    language: {
      names: {
        sr: 'Srpski',
        es: 'Španski',
        en: 'Engleski'
      },
      selectorLabel: 'Izaberite jezik'
    },
    theme: {
      light: 'Svetla',
      dark: 'Tamna',
      switchToLightMode: 'Prebaci na svetli režim',
      switchToDarkMode: 'Prebaci na tamni režim'
    },
    home: {
      hero: {
        badge: 'Cost-effective on-prem data platforma',
        title: 'Enterprise data infrastruktura bez enterprise cloud računa.',
        descriptionBeforeAccent:
          'DataSliv zamenjuje skupe cloud račune i rasute alate jednim ',
        descriptionAccent: 'predvidljivim',
        descriptionAfterAccent:
          ' slojem za unos, obradu, upravljanje i AI unutar vaše infrastrukture.',
        startMapping: 'Pogledajte platformu',
        getDemo: 'Zakažite demo',
        lifecycleStages: {
          ingest: 'Unos',
          govern: 'Kontrola',
          operate: 'Izvršavanje',
          monitor: 'Nadzor'
        }
      },
      technology: {
        benefitsTitle: 'Prednosti platforme na prvi pogled',
        benefitsAriaLabel: 'Prednosti platforme',
        technologies: [
          'Do 90% niži troškovi',
          'Bez cloud lock-ina',
          'Visoka dostupnost',
          'Automatski backup',
          'End-to-end enkripcija',
          'Početak od 30 minuta',
          'Sigurnost bankarskog nivoa',
          'SOC 2 Compliant',
          'Platforma spremna za AI',
          'Jednostavnija usklađenost',
          'Bez DevOps opterećenja',
          'Enterprise nivo'
        ],
        environmentsTitle: 'Napravljeno za okruženja osetljiva na podatke',
        environments: [
          'Finansijske usluge',
          'Telekom operateri',
          'Proizvodnja',
          'Zdravstvene grupe',
          'Javni sektor',
          'Energetika'
        ]
      },
      platform: {
        badge: 'Cost-effective platforma',
        title: 'Sve što data stack traži, bez gomile skupih servisa.',
        description:
          'DataSliv objedinjuje unos, obradu, storage, analitiku, warehouse, MLOps i AI u privatnom okruženju, pa plaćate predvidljivu platformu umesto više cloud računa, licenci i integracija. Netehnički timovi vide jasan trošak i rezultate; tehnički timovi zadržavaju kontrolu nad runtime-om, konektorima, lineage-om i politikama.',
        menuLabel: 'Meni platforme',
        navAriaLabel: 'Mogućnosti platforme',
        scrollToPrefix: 'Skroluj do',
        layerLabel: 'Sloj platforme',
        privateRuntime: 'Privatno izvršavanje',
        typicalPath: 'Tipičan tok',
        metrics: {
          layer: 'Sloj',
          controls: 'Kontrole',
          pathStages: 'Faze toka'
        },
        stepPrefix: 'Korak',
        capabilities: [
          {
            label: 'Unos',
            title: 'Unos podataka',
            lead: 'Dovedite svaki odobreni izvor u jedan kontrolisani ulazni sloj.',
            description:
              'Povežite baze podataka, API-je, fajlove, streamove i operativne sisteme bez premeštanja vlasništva van vašeg okruženja. Svaki izvor se registruje sa kredencijalima, kontekstom šeme, pravilima sinhronizacije i proverama zdravlja pre nego što podaci krenu.',
            outcomes: [
              'Batch, streaming i API unos',
              'Ugovori izvora i vlasništvo',
              'Nadzor konekcija i ponovni pokušaji'
            ],
            route: ['Izvori', 'Ugovori', 'Validacija', 'Landing zona']
          },
          {
            label: 'Obrada',
            title: 'Obrada podataka',
            lead: 'Pretvorite sirovo kretanje u ponovljive i vidljive transformacione poslove.',
            description:
              'Čistite, obogaćujte, spajajte i pripremajte podatke kroz zakazane tokove rada ili tokove pokrenute događajima. Timovi prate status poslova, neuspešna izvršavanja, ponovne pokušaje, lineage i SLA pritisak bez kopanja po odvojenim skriptama i logovima.',
            outcomes: [
              'Orkestracija za pipeline-ove i zadatke',
              'Ponovljivi obrasci transformacije',
              'Istorija izvršavanja, lineage i upozorenja'
            ],
            route: ['Validiraj', 'Transformiši', 'Obogati', 'Objavi']
          },
          {
            label: 'Data lake',
            title: 'Data lake',
            lead: 'Skladištite sirove i obrađene podatke u privatnoj, skalabilnoj lake osnovi.',
            description:
              'Držite velike količine strukturiranih, polustrukturiranih i nestrukturiranih podataka blizu svoje infrastrukture. DataSliv organizuje landing, bronze, silver i gold zone uz kontrolu pristupa, pravila zadržavanja i planiranje skladištenja svesno troškova.',
            outcomes: [
              'Zone za sirove i obrađene podatke',
              'Evolucija šeme i politika zadržavanja',
              'Privatni cloud ili on-prem instalacija'
            ],
            route: ['Raw zona', 'Curated zona', 'Katalog', 'Upravljanje']
          },
          {
            label: 'Analitika',
            title: 'Analitika podataka',
            lead: 'Dajte timovima pouzdane skupove podataka za izveštavanje, istraživanje i odluke.',
            description:
              'Izložite kontrolisane podatke BI dashboardima, notebookovima, operativnim izveštajima i self-service analizi. Analitičari dobijaju dokumentovane skupove podataka, dok platformski timovi zadržavaju kontrolu nad dozvolama, lineage-om i kvalitetom osvežavanja.',
            outcomes: [
              'BI-ready skupovi podataka i semantički pogledi',
              'Notebook i SQL istraživanje',
              'Definicije metrika i provere osvežavanja'
            ],
            route: ['Katalog', 'Metrike', 'Dashboardi', 'Uvidi']
          },
          {
            label: 'Skladište',
            title: 'Skladište podataka',
            lead: 'Isporučite poslovno kritično izveštavanje iz modelovanih, pouzdanih data martova.',
            description:
              'Modelujte obrađene podatke u warehouse tabele, dimenzione slojeve i domenske martove za finansije, operacije, proizvod i rukovodstvo. Compute i storage ostaju predvidljivi jer su warehouse opterećenja odvojena od aktivnosti sirovog lake-a.',
            outcomes: [
              'Dimenzioni modeli i martovi',
              'Planiranje compute-a po opterećenju',
              'Pouzdane tabele za izveštavanje'
            ],
            route: ['Modeluj', 'Optimizuj', 'Serviraj', 'Audit']
          },
          {
            label: 'MLOps',
            title: 'MLOps',
            lead: 'Premestite modele iz eksperimenata u kontrolisane, ponovljive operacije.',
            description:
              'Koordinirajte pripremu feature-a, trening poslove, registre modela, odobrenja, deployment i monitoring. Data scientisti mogu da iteriraju, dok operativni timovi zadržavaju vidljivu reproduktivnost, rollback putanje i dokaze usklađenosti.',
            outcomes: [
              'Feature i trening pipeline-ovi',
              'Registar modela i release provere',
              'Monitoring drifta i kvaliteta'
            ],
            route: ['Feature-i', 'Treniraj', 'Registruj', 'Nadgledaj']
          },
          {
            label: 'AI integracija',
            title: 'LLM i AI integracija',
            lead: 'Povežite privatne podatke sa AI tokovima rada bez gubitka kontrole.',
            description:
              'Pripremite kontrolisane skupove podataka, vektorske indekse, retrieval tokove i AI application hookove za interne asistente i automatizaciju. Osetljiv kontekst ostaje unutar odobrenih granica, dok timovi dobijaju prednosti modernih LLM i AI interfejsa.',
            outcomes: [
              'Privatni retrieval i vektorska pretraga',
              'Kontrolisan kontekst za AI aplikacije',
              'Ljudska provera i audit tragovi'
            ],
            route: ['Kontekst', 'Retrieval', 'Guardrails', 'AI aplikacije']
          }
        ]
      },
      inspira: {
        badge: 'Dokazana ušteda',
        company: 'Inspira Grupa',
        title: 'Inspira Grupa je smanjila trošak data platforme 18x.',
        description:
          'Umesto stalnog rasta računa kroz više servisa, DataSliv je konsolidovao ključne data tokove u privatnu platformu sa jasnim mesečnim modelom. Rezultat je priča koju razume i finansijski i tehnički tim: isti poslovni tokovi, mnogo niži trošak i više kontrole.',
        savingsLabel: 'Ostvarena ušteda',
        savingsValue: '18x',
        savingsDetail:
          'manji trošak u poređenju sa prethodnim pristupom za data platformu',
        proofPoints: [
          {
            title: 'Jasan poslovni efekat',
            description:
              'Trošak platforme postaje razgovor o povratu investicije, ne samo o infrastrukturi.'
          },
          {
            title: 'Tehnička kontrola ostaje',
            description:
              'Runtime, konektori, obrada, skladištenje i politike ostaju pod kontrolom tima.'
          },
          {
            title: 'Lakše planiranje rasta',
            description:
              'Predvidljiv model pomaže da se novi izvori i veći obimi planiraju bez iznenađenja na računu.'
          }
        ],
        cta: 'Proceni sličnu uštedu'
      },
      buildVsBuy: {
        badge: 'Build vs buy',
        title: 'Skriveni trošak iza jednostavnog toka podataka.',
        description:
          'Skripta može da premesti podatke za jedno popodne. Izgradnja kontrolisanog, vidljivog, on-prem operativnog sloja oko nje pretvara se u kvartale inženjerskog rada.',
        buildEyebrow: 'Gradnja interno',
        buildTitle: 'Šta timovi otkriju prekasno',
        buyEyebrow: 'DataSliv',
        buyTitle: 'Operativni sloj uključen',
        recommended: 'Preporučeno',
        stats: [
          {
            value: '4',
            label: 'operativna sloja',
            detail: 'mapiranje izvora, politika, operacije i provere isporuke'
          },
          {
            value: '30 min',
            label: 'do kontrolisanog početka',
            detail: 'mapirajte prvi tok rada pre velikog migracionog plana'
          },
          {
            value: '0',
            label: 'potreban cloud lock-in',
            detail: 'zadržite runtime on-prem ili unutar privatnog clouda'
          }
        ],
        buildCosts: [
          {
            title: 'Otkrivanje izvora',
            description:
              'Tabela sa bazama, vlasnicima, kredencijalima i šemama zastareva čim se timovi promene.'
          },
          {
            title: 'Sprovođenje politika',
            description:
              'Maskiranje, zadržavanje, odobrenja i izuzeci moraju ponovo da se grade oko svake nove putanje podataka.'
          },
          {
            title: 'Operativna primopredaja',
            description:
              'Ponovni pokušaji, redovi, zaduženja i SLA pritisak postaju custom platformski rad umesto rada sa podacima.'
          },
          {
            title: 'Spremnost za audit',
            description:
              'Istorija izvršavanja, odobrenja i dokazi izlaza završavaju razbacani po logovima, tiketima i chatovima.'
          },
          {
            title: 'Održavanje',
            description:
              'Svaka promena politike, drift šeme i nizvodni zahtev postaju još jedan inženjerski tiket.'
          },
          {
            title: 'Predvidljiv trošak',
            description:
              'Infrastruktura, code review, podrška i rešavanje sporova nastavljaju da rastu van jednog operativnog modela.'
          }
        ],
        buyAdvantages: [
          {
            title: 'Privatna mapa izvora',
            description:
              'Registrujte baze, fajlove, streamove i servise sa vlasništvom i kontekstom konekcije na jednom mestu.'
          },
          {
            title: 'Tokovi rada vođeni politikama',
            description:
              'Puštajte kretanje podataka kroz odobrenja, maskiranje, zadržavanje i provere izuzetaka pre izvršavanja poslova.'
          },
          {
            title: 'Vidljive operacije',
            description:
              'Pratite redove, ponovne pokušaje, vlasništvo, SLA pritisak i status izvršavanja bez izlaganja privatnih sistema.'
          },
          {
            title: 'Audit istorija',
            description:
              'Čuva pretražive dokaze za izvore, odluke, poslove i izlaze povezane sa svakim tokom rada.'
          },
          {
            title: 'Kontrolisana isporuka',
            description:
              'Šaljite odobrene podatke u BI, exporte, notebookove, API-je i aplikacije uz sačuvanu istoriju isporuke.'
          },
          {
            title: 'Predvidljiv footprint',
            description:
              'Počnite ograničeno, širite tok po tok i modelujte compute uz jasnu cenu platforme.'
          }
        ],
        ctaText:
          'Želite da vidite gde se DataSliv uklapa u vaš trenutni stack?',
        explorePlatform: 'Istražite platformu',
        estimateCost: 'Proceni trošak'
      },
      calculator: {
        badge: 'Kalkulator',
        title: 'Cena zasnovana na compute unitima',
        description:
          'Podesite compute jedinice, skladište i mesečni obim upita da vidite DataSliv trošak i cloud poređenje u jednom prikazu. Cloud poređenje prati logiku iz dostavljenog članka: troškovi upita plus storage provajdera.',
        totalMonthlyCost: 'Ukupan mesečni trošak',
        controls: {
          computeUnits: {
            label: 'Compute jedinice',
            suffix: 'compute jedinice'
          },
          dataVolume: {
            label: 'Količina podataka',
            suffix: 'TB',
            detailAt: 'po'
          },
          monthlyQueries: {
            label: 'Procena mesečnih upita',
            suffix: 'upita / mesec',
            included: 'Uključeno u DataSliv'
          }
        },
        savingsVsCloud: 'Ušteda u odnosu na cloud',
        premiumVsCloud: 'Premija u odnosu na cloud',
        comparedTo: 'u poređenju sa',
        costBreakdown: 'Raspodela troškova',
        customPlanQuestion: 'Treba vam prilagođen plan za veća uvođenja?',
        getInContact: 'Kontaktirajte nas',
        queryUnit: 'upit',
        chartAriaLabel: 'Stubičasti grafikon mesečnog troška',
        chartShortLabels: {
          'Platform fee': 'Platforma',
          'Compute Units Cost': 'Compute',
          'Storage Cost': 'Storage',
          'Microsoft Fabric': 'Fabric'
        },
        breakdownRows: {
          platformFee: 'Naknada za platformu',
          computeUnitsCost: 'Trošak compute jedinica',
          storageCost: 'Trošak skladišta',
          monthlyPlatformTotal: 'Ukupan mesečni trošak platforme'
        },
        chart: {
          dataSlivCostGraph: 'DataSliv graf troškova',
          cloudComparisonGraph: 'Poređenje troškova',
          breakdownDescription:
            'Naknada za platformu, trošak compute jedinica i trošak skladišta.',
          comparisonDescriptionPrefix:
            'Na osnovu logike iz članka: cloud procene primenjuju trošak po upitu na',
          comparisonDescriptionSuffix:
            'mesečnih upita, a zatim dodaju storage provajdera po TB.',
          threeValues: '3 vrednosti',
          queriesPlusStorage: 'Upiti + storage',
          compareCloud: 'Poređenje troškova',
          dataSlivGraph: 'DataSliv graf'
        },
        source: {
          basedOnArticle: 'Na osnovu članka:',
          by: 'autor',
          benchmarkUses: 'Benchmark koristi',
          workload: 'benchmark coffee shop sa 3,18 milijardi redova',
          reportedCostPrefix: 'prijavljeni ukupan trošak za',
          reportedCostSuffix:
            'upita koristi se kao ulaz za cenu po upitu, zatim se dodaje storage provajdera po TB.'
        },
        customComparison: {
          title: 'Prilagođeno poređenje',
          description:
            'Želite da uporedimo stvarni Fabric, Databricks ili Snowflake setup sa DataSlivom?'
        },
        computeUnitModel: 'Model compute jedinica',
        computeUnitServices: [
          'Spark izvršilac',
          'Kafka broker',
          'Trino izvršilac',
          'Airflow izvršilac'
        ],
        unitsAbbreviation: 'CU'
      },
      compliance: {
        eyebrow: 'POVERENJE I USKLAĐENOST',
        title: 'Poverenje u samoj osnovi.',
        description:
          'Dizajnirano uz uvažavanje bezbednosti, privatnosti i regulatornih zahteva.',
        listLabel: 'Relevantni standardi i regulatorni okviri',
        note: 'Relevantni standardi i okviri — ne predstavljaju tvrdnju o sertifikaciji ili usklađenosti.',
        items: {
          gdpr: 'Zaštita podataka',
          soc2: 'Kontrole poverenja',
          iso27001: 'Bezbednost informacija',
          hipaa: 'Privatnost zdravstvenih podataka',
          ccpa: 'Privatnost potrošača'
        }
      },
      security: {
        badge: 'On-prem operacije',
        title: 'Spremni da probate DataSliv?',
        bookDemo: 'Zakažite demo',
        infrastructureCards: [
          {
            title: 'Privatne putanje podataka',
            description:
              'Povežite izvore, poslove i slojeve izveštavanja kroz interne mreže.'
          },
          {
            title: 'Kontrolisano izvršavanje',
            description:
              'Pokrećite kontrolisane tokove rada uz odobrenja, vlasništvo i jasan operativni status.'
          },
          {
            title: 'Aktivnost spremna za audit',
            description:
              'Pratite ko je šta promenio, kada su se poslovi pomerili i gde su nastali izuzeci.'
          },
          {
            title: 'Kontrole politika',
            description:
              'Držite pravila tokova rada, putanje pristupa i uslove objave eksplicitnim.'
          },
          {
            title: 'Operativna vidljivost',
            description:
              'Dajte timovima jedno mesto za kretanje podataka, greške, odobrenja i zavisnosti.'
          }
        ]
      },
      contactSection: {
        title:
          'Dovedite moderan sloj tokova podataka u svoje on-prem okruženje.',
        description:
          'Podelite tok rada koji želite da kontrolišete i javićemo vam se sa praktičnim putem za vaše okruženje.',
        signal:
          'Discovery počinje od trenutnog kretanja podataka, odobrenja i uskih grla u izveštavanju.',
        getInContact: 'Kontaktirajte nas'
      }
    },
    insights: {
      heroBadge: 'Insights',
      title: 'Praktični uvidi za privatne data platforme.',
      description:
        'Kratki tekstovi o trošku, kontroli i arhitekturi on-prem data platformi za timove koji žele manje iznenađenja u produkciji.',
      latestLabel: 'Poslednje objave',
      loadingPosts: 'Učitavanje objava…',
      emptyFeedTitle: 'Trenutno nema blogova ili vesti.',
      emptyFeedDescription:
        'Nove objave će se pojaviti ovde čim budu dostupne.',
      linksLabel: 'Linkovi',
      allInsights: 'Svi insight-i',
      quickLinks: [
        {
          label: 'Blogovi',
          to: '/insights#latest-insights'
        },
        {
          label: 'Primeri',
          to: '/cases'
        },
        {
          label: 'Kontakt',
          to: '/contact'
        }
      ],
      filters: {
        title: 'Filtrirajte objave',
        description:
          'Prikažite blogove ili vesti po tipu sadržaja, servisima i industrijama.',
        typeLabel: 'Tip sadržaja',
        serviceLabel: 'Servisi',
        industryLabel: 'Industrije',
        allTypes: 'Sve objave',
        allServices: 'Svi servisi',
        allIndustries: 'Sve industrije',
        reset: 'Resetuj filtere',
        results: 'rezultata',
        emptyTitle: 'Nema objava za izabrane filtere.',
        emptyDescription: 'Probajte drugi tip sadržaja, servis ili industriju.',
        previous: 'Prethodna',
        next: 'Sledeća',
        page: 'Strana',
        of: 'od',
        typeOptions: [
          {
            label: 'Blog',
            value: 'blog'
          },
          {
            label: 'Vest',
            value: 'news'
          }
        ],
        serviceOptions: [
          {
            label: 'Platforma',
            value: 'platform'
          },
          {
            label: 'Kalkulator troška',
            value: 'cost-calculator'
          },
          {
            label: 'Governance',
            value: 'governance'
          },
          {
            label: 'AI i analitika',
            value: 'ai-analytics'
          },
          {
            label: 'Konektori',
            value: 'connectors'
          },
          {
            label: 'Security',
            value: 'security'
          }
        ],
        industryOptions: [
          {
            label: 'Enterprise',
            value: 'enterprise'
          },
          {
            label: 'Javni sektor',
            value: 'public-sector'
          },
          {
            label: 'Telekom',
            value: 'telecom'
          },
          {
            label: 'Finansije',
            value: 'finance'
          },
          {
            label: 'Transport',
            value: 'transport'
          },
          {
            label: 'Retail',
            value: 'retail'
          }
        ]
      },
      finalCta: {
        title: 'Želite insight za svoj trenutni data stack?',
        description:
          'Pošaljite nam kako danas unosite, obrađujete i kontrolišete podatke, pa ćemo mapirati gde trošak i operacije mogu da se pojednostave.',
        contact: 'Razgovarajte sa timom'
      }
    },
    blog: {
      backToInsights: 'Nazad na insights',
      breadcrumbHome: 'Početna',
      breadcrumbInsights: 'Insights',
      published: 'Objavljeno',
      updated: 'Ažurirano',
      author: 'Autor',
      readTime: 'Vreme čitanja',
      service: 'Servis',
      industry: 'Industrija',
      relatedTitle: 'Povezane objave',
      relatedDescription:
        'Još tekstova iz sličnih servisa i industrijskih konteksta.',
      readArticle: 'Pročitajte objavu',
      articleType: 'Tip objave',
      loadingArticle: 'Učitavanje objave…',
      loadErrorTitle: 'Objava trenutno nije dostupna',
      loadErrorDescription:
        'Veza sa Blogger servisom nije uspela. Pokušajte ponovo za nekoliko trenutaka.',
      authorRoleFallback: 'DataSliv autor',
      tags: 'Teme',
      share: 'Podelite',
      shareOnLinkedIn: 'Podelite na LinkedIn-u',
      shareOnX: 'Podelite na X-u',
      shareByEmail: 'Podelite emailom',
      copyLink: 'Kopirajte link',
      linkCopied: 'Link je kopiran',
      continueReading: 'Nastavite sa čitanjem',
      browseAllInsights: 'Pogledajte sve insight-e',
      articleImage: 'Slika iz objave'
    },
    cases: {
      heroBadge: 'Primeri',
      title: 'DataSliv case-ovi gde trošak i kontrola odlučuju.',
      description:
        'Imamo četiri relevantna DataSliv primera: dve javne reference koje možemo da imenujemo i dva poverljiva on-prem okruženja koja prikazujemo kroz problem koji su rešavala. Zajednički imenilac je jednostavan: znatno niži trošak, suverenitet podataka i platforma bazirana na open source principima.',
      estimateCost: 'Proceni trošak',
      explorePlatform: 'Istražite platformu',
      heroSelectorPrefix: 'DataSliv za',
      heroCategoryAriaLabel: 'Izaberite fokus case stranice',
      heroCategories: [
        'Optimizaciju troška',
        'Privatne data platforme',
        'Data engineering',
        'Governance',
        'AI-ready infrastrukturu'
      ],
      heroPrimaryCta: 'Pogledajte case-ove',
      calculateSavings: 'Izračunajte uštedu',
      bookDemo: 'Zakažite demo',
      platformName: 'DataSliv platforma',
      heroVisual: {
        sourceLabel: 'Izvori podataka',
        outcomeLabel: 'Ishodi',
        sources: ['CRM', 'Baze', 'Fajlovi', 'Događaji', 'API-ji'],
        platformSteps: ['Ingest', 'Process', 'Govern', 'Monitor'],
        outcomes: ['Niži trošak', 'Privatna infrastruktura', 'AI-ready podaci']
      },
      outcomeOverview: {
        title: 'Zašto timovi biraju DataSliv',
        cards: [
          {
            value: 'Trošak',
            title: 'Predvidljiv platformski trošak',
            description:
              'Zamenite rasute servise i varijabilnu naplatu jednim jasnim operativnim modelom.'
          },
          {
            value: 'Kontrola',
            title: 'Infrastruktura pod vašom kontrolom',
            description:
              'Zadržite runtime, konektore, skladište, obradu i politike unutar svog okruženja.'
          },
          {
            value: 'Skala',
            title: 'Jednostavniji put do skaliranja',
            description:
              'Dodajte nove izvore i workload-e bez prepravke platforme oko svakog novog alata.'
          }
        ]
      },
      featured: {
        selectorLabel:
          'Za isti poslovni tok, konsolidovana privatna platforma pokazuje koliko se platformski trošak može smanjiti.',
        selectorAriaLabel: 'Izaberite DataSliv case',
        eyebrow: 'Dokaz uštede',
        proofPoint:
          '18x niži trošak u odnosu na prethodni pristup data platformi',
        cta: 'Izračunajte potencijalnu uštedu'
      },
      workflowTitle: 'Dve javne reference, dva anonimizovana case-a',
      workflowDescription:
        'Inspira i Cropt mogu da se prikažu imenom. Preostala dva case-a opisana su kao telekom i pravosudno okruženje, jer je važnije pokazati zašto je DataSliv izabran: on-prem rad, suverenitet podataka i open-core osnova.',
      workflowSteps: [
        'Inspira Grupa - javna referenca',
        'Cropt - javna referenca',
        'Telekom okruženje - anonimizovano',
        'Pravosudno okruženje - anonimizovano'
      ],
      sectionBadge: 'DataSliv u praksi',
      sectionTitle: 'Javne reference i poverljivi primeri.',
      patternBadge: 'Zašto su timovi birali DataSliv',
      patternTitle: 'Niži trošak bez odustajanja od kontrole.',
      patternDescription:
        'Case-ovi nisu samo tehničke implementacije. U praksi se odluka svodi na to da platforma mora biti jeftinija od slaganja više servisa, mora ostati pod kontrolom korisnika i mora moći da radi u privatnom okruženju.',
      capabilityBadge: 'Mogućnosti platforme',
      capabilities: [
        {
          eyebrow: 'Predvidljive operacije',
          title: 'Pokrenite kritične data workflow-e na predvidljivom modelu',
          description:
            'Pomerite ingestion i obradu dalje od rasutih usage-based servisa. Planirajte kapacitet, izvore i workload-e kroz jednu privatnu platformu sa jasnijim operativnim troškovima.',
          cta: 'Izračunajte potencijalnu uštedu',
          metrics: ['Plan poslova', 'Status runtime-a', 'Forecast troška']
        },
        {
          eyebrow: 'Konsolidacija',
          title: 'Zamenite fragmentiran tool stack jednom platformom',
          description:
            'Dovedite konektore, obradu, orkestraciju, governance i monitoring u jedan operativni sloj umesto održavanja zasebnih servisa za svaki workflow.',
          cta: 'Pogledajte kako radi',
          metrics: ['Jedan core', 'Manje handoff-a', 'Više reuse-a']
        },
        {
          eyebrow: 'Privatna kontrola',
          title: 'Zadržite podatke, politike i runtime pod svojom kontrolom',
          description:
            'Postavite DataSliv unutar svoje infrastrukture i zadržite vlasništvo nad skladištem, access politikama, obradom i operativnim odlukama.',
          cta: 'Pogledajte arhitekturu',
          metrics: ['Policy layer', 'Role access', 'Audit događaji']
        },
        {
          eyebrow: 'AI-ready osnova',
          title: 'Izgradite AI-ready osnovu bez dupliranja platforme',
          description:
            'Koristite upravljane i obrađene podatke za analitiku, machine learning i AI operacije bez slanja svakog workload-a kroz još jedan nepovezan servis.',
          cta: 'Istražite platformu',
          metrics: ['Lineage', 'Quality checks', 'Trusted features']
        },
        {
          eyebrow: 'Skaliranje',
          title: 'Dodajte izvore i workload-e bez iznenađenja u naplati',
          description:
            'Povežite nove sisteme, povećajte obim podataka i proširite obradu dok arhitektura i operativni troškovi ostaju lakši za razumevanje.',
          cta: 'Kontaktirajte naš tim',
          metrics: ['Rastući izvori', 'Stabilan core', 'Jasan kapacitet']
        }
      ],
      carousel: {
        title: 'Šta timovi dobijaju uz DataSliv',
        description:
          'Kada nema verifikovanog customer citata, prikazujemo proverljive operativne ishode umesto izmišljenih izjava.',
        previous: 'Prethodni ishod',
        next: 'Sledeći ishod',
        selectorAriaLabel: 'Izaberite ishod',
        cards: [
          {
            title: 'Finansije dobijaju predvidljivo planiranje troška',
            description:
              'Timovi mogu da porede realne workload-e sa jednim privatnim modelom umesto da jure promenljive račune kroz više servisa.'
          },
          {
            title: 'Engineering zadržava runtime kontrolu',
            description:
              'Konektori, obrada, politike i operativni status ostaju blizu infrastrukture koju tim već poznaje.'
          },
          {
            title: 'Novi izvori se lakše dodaju',
            description:
              'Platformski obrazac se ponovo koristi za sledeći izvor, pipeline ili tim, umesto da svaki zahtev postane poseban projekat.'
          },
          {
            title: 'Governance ostaje deo platforme',
            description:
              'Policy provere, role boundaries i audit događaji prate tok podataka od ingest-a do isporuke.'
          },
          {
            title: 'AI workload-i koriste trusted data osnovu',
            description:
              'Obrađeni, upravljani i traceable skupovi podataka mogu da podrže AI bez paralelnog data stack-a.'
          }
        ]
      },
      finalCta: {
        title: 'Pogledajte koliko vas trenutna data platforma zaista košta.',
        description:
          'Uporedite rasute cloud servise sa predvidljivom privatnom platformom izgrađenom oko vaših realnih workload-a.'
      },
      caseCards: [
        {
          eyebrow: 'Javna referenca',
          title: 'Inspira Grupa',
          summary:
            'DataSliv je zamenio skuplji pristup data platformi privatnim, konsolidovanim rešenjem. Za isti poslovni tok, Inspira je dobila 18x niži trošak u odnosu na prethodni model, uz jasniji mesečni okvir i više kontrole nad podacima.',
          outcomes: [
            '18x niži platformski trošak',
            'Predvidljiv mesečni model',
            'Zadržana tehnička kontrola'
          ]
        },
        {
          eyebrow: 'Javna referenca',
          title: 'Cropt',
          summary:
            'Cropt koristi DataSliv kao ekonomičniji način da objedini prikupljanje, obradu i analitiku podataka bez gomilanja skupih servisa. Fokus je na bržem putu od izvora do korisnog uvida, uz trošak koji ostaje pod kontrolom.',
          outcomes: [
            'Niži platformski trošak',
            'Konsolidovani tokovi',
            'Analitika spremna za rast'
          ]
        },
        {
          eyebrow: 'Poverljiv case',
          title: 'Telekom okruženje sa strogim zahtevima',
          summary:
            'Za telekom okruženje sa visokim zahtevima za suverenitet podataka, DataSliv se postavlja on-prem i oslanja na open-core arhitekturu baziranu na open source komponentama. Podaci, runtime i operacije ostaju pod kontrolom korisnika.',
          outcomes: [
            'Suverenitet podataka',
            'On-prem deployment',
            'Open-core osnova'
          ]
        },
        {
          eyebrow: 'Poverljiv case',
          title: 'Pravosudno okruženje',
          summary:
            'U pravosudnom okruženju prioritet je da osetljivi podaci ne napuštaju odobrenu infrastrukturu. DataSliv omogućava privatne tokove rada, audit trag i rešenje zasnovano na open source principima, bez zavisnosti od zatvorenog cloud stack-a.',
          outcomes: [
            'Privatni tokovi rada',
            'Audit i kontrola',
            'Open source princip'
          ]
        }
      ],
      operatingPatterns: [
        {
          title: 'Jedna platforma umesto skupog slaganja servisa',
          description:
            'DataSliv objedinjuje tokove podataka u predvidljiv model, pa cost-effectiveness postaje glavna poruka umesto još jedne licence, integracije ili cloud računa.'
        },
        {
          title: 'Suverenitet podataka u privatnom okruženju',
          description:
            'Za organizacije koje ne smeju da izgube kontrolu nad podacima, konektorima i runtime-om, DataSliv ostaje on-prem i radi unutar odobrene infrastrukture.'
        },
        {
          title: 'Open-core osnova bez zatvorenog lock-ina',
          description:
            'Platforma je bazirana na open source principima, uz produkcioni sloj koji omogućava podršku, kontrolu i širenje bez potpunog oslanjanja na zatvoren cloud stack.'
        }
      ]
    },
    contact: {
      badge: 'Kontakt',
      title: 'Recite nam šta vaš tok podataka mora da podrži.',
      description:
        'Pošaljite kratku poruku o svom okruženju, kretanju podataka koje treba da kontrolišete ili timu koji želite da podržite. Javićemo vam se emailom.',
      supportPoints: [
        'Discovery privatnih tokova podataka',
        'Pitanja o on-prem instalaciji',
        'Zahtevi za upravljanje i audit'
      ],
      explorePlatform: 'Istražite platformu',
      formTitle: 'Kontaktirajte nas',
      mailerWarningBeforeCode: 'Dodajte',
      mailerWarningAfterCode: 'da omogućite slanje kontakt forme.',
      labels: {
        company: 'Kompanija',
        firstName: 'Ime *',
        lastName: 'Prezime *',
        email: 'Email *',
        message: 'Poruka *'
      },
      sendMessage: 'Pošalji poruku',
      validation: {
        firstNameMin: 'Ime mora imati najmanje 2 karaktera.',
        firstNameMax: 'Ime može imati najviše 80 karaktera.',
        firstNamePattern:
          'Ime može sadržati samo slova, razmake, crtice ili apostrofe.',
        lastNameMin: 'Prezime mora imati najmanje 2 karaktera.',
        lastNameMax: 'Prezime može imati najviše 80 karaktera.',
        lastNamePattern:
          'Prezime može sadržati samo slova, razmake, crtice ili apostrofe.',
        emailInvalid: 'Unesite važeću email adresu.',
        emailMax: 'Email može imati najviše 254 karaktera.',
        messageMin: 'Poruka mora imati najmanje 5 karaktera.',
        messageMax: 'Poruka može imati najviše 2000 karaktera.'
      },
      notifications: {
        received: 'Hvala, vaša poruka je primljena.',
        mailerMissing:
          'Kontakt mailer nije konfigurisan. Dodajte VITE_GOOGLE_MAILER_URL da omogućite slanje.',
        sent: 'Hvala, vaša poruka je poslata.',
        failed: 'Nešto je pošlo po zlu pri slanju poruke. Pokušajte ponovo.'
      }
    },
    notFound: {
      routeSignal: 'Signal rute',
      live: 'aktivno',
      connectionLost: 'Veza izgubljena',
      checkingRoute: 'Provera rute',
      pageBadge: '404 strana',
      routeCheckBadge: 'Provera rute',
      title: 'Ruta je prekinuta.',
      description:
        'Ova adresa nije deo trenutne DataSliv mape. Ruta se nakratko razreši, a zatim signalni tok prelazi u 404 stanje.',
      goHome: 'Na početnu',
      goBack: 'Nazad',
      requestedRoute: 'Tražena ruta',
      explorePlatformTitle: 'Istražite platformu',
      explorePlatformDescription: 'Počnite od DataSliv pregleda.',
      viewCasesTitle: 'Pogledajte primere',
      viewCasesDescription: 'Vidite uobičajene on-prem tokove podataka.',
      contactTeamTitle: 'Kontaktirajte tim',
      contactTeamDescription: 'Recite nam šta ste pokušali da pronađete.'
    },
    errorBoundary: {
      title: 'Ups! Nešto je pošlo po zlu',
      description: 'Naišli smo na neočekivanu grešku. Ne brinite, nije do vas.',
      detailsSummary: 'Detalji greške (dev režim)',
      tryAgain: 'Pokušaj ponovo',
      reloadPage: 'Ponovo učitaj stranicu',
      goHome: 'Na početnu'
    }
  },
  es: {
    meta: {
      title: 'DataSliv - solucion de datos on-prem',
      description:
        'DataSliv es una solucion de datos on-prem para ingestion privada, gobernanza, automatizacion de flujos y reporting dentro de tu propia infraestructura.',
      keywords:
        'DataSliv, datos on-prem, plataforma privada de datos, gobernanza de datos, automatizacion de flujos, operaciones de datos',
      ogDescription:
        'Un plano de control privado para ingestion de datos, gobernanza, automatizacion y reporting dentro de tu propia infraestructura.',
      imageAlt: 'Logo de DataSliv',
      twitterDescription:
        'Un plano de control privado para flujos de datos on-prem.'
    },
    common: {
      backToTop: 'Volver arriba',
      close: 'Cerrar',
      dataSliv: 'DataSliv',
      soc2Compliant: 'SOC 2 Compliant',
      month: 'mes',
      year: 'año',
      total: 'Total'
    },
    nav: {
      logoAlt: 'Logo de DataSliv',
      primaryAriaLabel: 'Navegacion principal',
      openMenu: 'Abrir menu',
      closeMenu: 'Cerrar menu',
      platform: 'Plataforma',
      calculator: 'Calculadora',
      cases: 'Casos',
      insights: 'Insights',
      insightsDropdownAriaLabel: 'Menu de insights',
      contact: 'Contacto',
      demoShort: 'Demo',
      demoLong: 'Solicitar demo'
    },
    footer: {
      tagline: 'DataSliv - operaciones de datos on-prem'
    },
    language: {
      names: {
        sr: 'Serbio',
        es: 'Español',
        en: 'Inglés'
      },
      selectorLabel: 'Seleccionar idioma'
    },
    theme: {
      light: 'Claro',
      dark: 'Oscuro',
      switchToLightMode: 'Cambiar a modo claro',
      switchToDarkMode: 'Cambiar a modo oscuro'
    },
    home: {
      hero: {
        badge: 'Plataforma de datos on-prem coste-efectiva',
        title:
          'Infraestructura de datos empresarial sin facturas cloud empresariales.',
        descriptionBeforeAccent:
          'DataSliv sustituye facturas cloud crecientes y herramientas dispersas por una capa ',
        descriptionAccent: 'predecible',
        descriptionAfterAccent:
          ' para ingesta, procesamiento, gobierno y AI dentro de tu infraestructura.',
        startMapping: 'Ver plataforma',
        getDemo: 'Solicitar demo',
        lifecycleStages: {
          ingest: 'Ingesta',
          govern: 'Gobierno',
          operate: 'Operar',
          monitor: 'Monitoreo'
        }
      },
      technology: {
        benefitsTitle: 'Beneficios de la plataforma de un vistazo',
        benefitsAriaLabel: 'Beneficios de la plataforma',
        technologies: [
          'Hasta 90% menos coste',
          'Sin dependencia de cloud',
          'Alta disponibilidad',
          'Backups automatizados',
          'Cifrado end-to-end',
          'Inicio desde 30 minutos',
          'Seguridad de nivel bancario',
          'SOC 2 Compliant',
          'Plataforma lista para AI',
          'Compliance simplificado',
          'Sin DevOps requerido',
          'Nivel enterprise'
        ],
        environmentsTitle:
          'Construido para entornos empresariales sensibles a los datos',
        environments: [
          'Servicios financieros',
          'Operadores telecom',
          'Manufactura',
          'Grupos sanitarios',
          'Sector publico',
          'Energia'
        ]
      },
      platform: {
        badge: 'Plataforma coste-efectiva',
        title:
          'Todo lo que necesita el data stack, sin tantos servicios caros.',
        description:
          'DataSliv une ingesta, procesamiento, storage, analitica, warehouse, MLOps y AI en un entorno privado, para pagar una plataforma predecible en lugar de varias facturas cloud, licencias e integraciones. Los equipos no tecnicos ven coste y resultado claros; los equipos tecnicos conservan control sobre runtime, conectores, lineage y politicas.',
        menuLabel: 'Menu de plataforma',
        navAriaLabel: 'Capacidades de la plataforma',
        scrollToPrefix: 'Ir a',
        layerLabel: 'Capa de plataforma',
        privateRuntime: 'Runtime privado',
        typicalPath: 'Ruta tipica',
        metrics: {
          layer: 'Capa',
          controls: 'Controles',
          pathStages: 'Etapas de ruta'
        },
        stepPrefix: 'Paso',
        capabilities: [
          {
            label: 'Ingesta',
            title: 'Ingesta de datos',
            lead: 'Lleva cada fuente aprobada a una capa de entrada controlada.',
            description:
              'Conecta bases de datos, APIs, archivos, streams y sistemas operativos sin sacar la propiedad de tu entorno. Cada fuente se registra con credenciales, contexto de esquema, reglas de sincronizacion y checks de salud antes de que los datos empiecen a fluir.',
            outcomes: [
              'Ingesta batch, streaming y API',
              'Contratos de fuente y propiedad',
              'Monitoreo de conexiones y reintentos'
            ],
            route: ['Fuentes', 'Contratos', 'Validacion', 'Landing zone']
          },
          {
            label: 'Procesamiento',
            title: 'Procesamiento de datos',
            lead: 'Convierte el movimiento bruto en trabajos de transformacion repetibles y observables.',
            description:
              'Limpia, enriquece, une y prepara datos mediante flujos programados o activados por eventos. Los equipos pueden seguir estado de jobs, ejecuciones fallidas, reintentos, lineage y presion de SLA sin revisar scripts y logs separados.',
            outcomes: [
              'Orquestacion para pipelines y tareas',
              'Patrones de transformacion reutilizables',
              'Historial de ejecucion, lineage y alertas'
            ],
            route: ['Validar', 'Transformar', 'Enriquecer', 'Publicar']
          },
          {
            label: 'Data lake',
            title: 'Data lake',
            lead: 'Almacena datos brutos y curados en una base lake privada y escalable.',
            description:
              'Mantén datos estructurados, semiestructurados y no estructurados de alto volumen cerca de tu infraestructura. DataSliv organiza zonas landing, bronze, silver y gold con control de acceso, reglas de retencion y planificacion de storage consciente del coste.',
            outcomes: [
              'Zonas de almacenamiento bruto y curado',
              'Evolucion de esquema y politica de retencion',
              'Despliegue en cloud privado u on-prem'
            ],
            route: ['Zona raw', 'Zona curada', 'Catalogo', 'Gobernanza']
          },
          {
            label: 'Analitica',
            title: 'Analitica de datos',
            lead: 'Entrega datasets confiables para reporting, exploracion y decisiones.',
            description:
              'Expone datos gobernados a dashboards BI, notebooks, reportes operativos y analisis self-service. Los analistas reciben datasets documentados mientras los equipos de plataforma mantienen control de permisos, lineage y calidad de refresco.',
            outcomes: [
              'Datasets listos para BI y vistas semanticas',
              'Exploracion con notebooks y SQL',
              'Definiciones de metricas y checks de refresco'
            ],
            route: ['Catalogo', 'Metricas', 'Dashboards', 'Insights']
          },
          {
            label: 'Warehouse',
            title: 'Data warehouse',
            lead: 'Sirve reporting critico de negocio desde data marts modelados y confiables.',
            description:
              'Modela datos curados en tablas warehouse, capas dimensionales y marts por dominio para finanzas, operaciones, producto y liderazgo. Compute y storage se mantienen predecibles porque las cargas warehouse se separan de la actividad raw del lake.',
            outcomes: [
              'Modelos dimensionales y marts',
              'Planificacion de compute por carga',
              'Tablas confiables para reporting'
            ],
            route: ['Modelar', 'Optimizar', 'Servir', 'Auditar']
          },
          {
            label: 'MLOps',
            title: 'MLOps',
            lead: 'Mueve modelos desde experimentos a operaciones gobernadas y repetibles.',
            description:
              'Coordina preparacion de features, jobs de entrenamiento, registro de modelos, aprobaciones, despliegues y monitoreo. Los data scientists pueden iterar mientras los equipos operativos mantienen visibles la reproducibilidad, rollback y evidencia de compliance.',
            outcomes: [
              'Pipelines de features y entrenamiento',
              'Registro de modelos y checks de release',
              'Monitoreo de drift y calidad'
            ],
            route: ['Features', 'Entrenar', 'Registrar', 'Monitorear']
          },
          {
            label: 'Integracion AI',
            title: 'Integracion LLM y AI',
            lead: 'Conecta datos privados a flujos AI sin perder control.',
            description:
              'Prepara datasets gobernados, indices vectoriales, flujos de retrieval y hooks de aplicaciones AI para asistentes internos y automatizacion. El contexto sensible permanece dentro de limites aprobados mientras los equipos aprovechan interfaces modernas de LLM y AI.',
            outcomes: [
              'Retrieval privado y busqueda vectorial',
              'Contexto gobernado para aplicaciones AI',
              'Revision humana y trazas de auditoria'
            ],
            route: ['Contexto', 'Retrieval', 'Guardrails', 'Apps AI']
          }
        ]
      },
      inspira: {
        badge: 'Ahorro probado',
        company: 'Inspira Grupa',
        title: 'Inspira Grupa redujo el coste de su plataforma de datos 18x.',
        description:
          'En lugar de seguir acumulando coste entre varios servicios, DataSliv consolidó los flujos clave en una plataforma privada con un modelo mensual claro. El resultado habla tanto a finanzas como a tecnologia: los mismos flujos de negocio, mucho menos coste y mas control.',
        savingsLabel: 'Ahorro conseguido',
        savingsValue: '18x',
        savingsDetail:
          'menor coste comparado con el enfoque anterior de plataforma de datos',
        proofPoints: [
          {
            title: 'Impacto de negocio claro',
            description:
              'El coste de la plataforma se convierte en una conversacion de ROI, no solo de infraestructura.'
          },
          {
            title: 'El control tecnico permanece',
            description:
              'Runtime, conectores, procesamiento, storage y politicas siguen bajo control del equipo.'
          },
          {
            title: 'Planificacion de crecimiento mas simple',
            description:
              'Un modelo predecible ayuda a planificar nuevas fuentes y mayor volumen sin sorpresas en la factura.'
          }
        ],
        cta: 'Estimar un ahorro similar'
      },
      buildVsBuy: {
        badge: 'Build vs buy',
        title: 'El coste oculto detras de un flujo de datos simple.',
        description:
          'Un script puede mover datos en una tarde. Construir alrededor una capa operativa on-prem, gobernada y observable, es lo que termina convirtiendose en trimestres de trabajo de ingenieria.',
        buildEyebrow: 'Construir interno',
        buildTitle: 'Lo que los equipos descubren tarde',
        buyEyebrow: 'DataSliv',
        buyTitle: 'Capa operativa incluida',
        recommended: 'Recomendado',
        stats: [
          {
            value: '4',
            label: 'capas operativas',
            detail:
              'mapeo de fuentes, politicas, operaciones y checks de entrega'
          },
          {
            value: '30 min',
            label: 'hasta un inicio contenido',
            detail: 'mapea el primer flujo antes de un gran plan de migracion'
          },
          {
            value: '0',
            label: 'lock-in cloud requerido',
            detail: 'mantén el runtime on-prem o dentro de un cloud privado'
          }
        ],
        buildCosts: [
          {
            title: 'Descubrimiento de fuentes',
            description:
              'Una hoja con bases de datos, responsables, credenciales y esquemas queda obsoleta en cuanto cambian los equipos.'
          },
          {
            title: 'Aplicacion de politicas',
            description:
              'Enmascaramiento, retencion, aprobaciones y excepciones deben reconstruirse alrededor de cada nueva ruta de datos.'
          },
          {
            title: 'Traspasos operativos',
            description:
              'Reintentos, colas, asignaciones y presion de SLA se vuelven trabajo de plataforma a medida en lugar de trabajo de datos.'
          },
          {
            title: 'Preparacion para auditoria',
            description:
              'Historial de ejecucion, aprobaciones y evidencia de salidas terminan dispersos entre logs, tickets y chats.'
          },
          {
            title: 'Mantenimiento',
            description:
              'Cada cambio de politica, drift de esquema y solicitud downstream se convierte en otro ticket de ingenieria.'
          },
          {
            title: 'Coste predecible',
            description:
              'Infraestructura, code review, soporte y gestion de disputas siguen creciendo fuera de un unico modelo operativo.'
          }
        ],
        buyAdvantages: [
          {
            title: 'Mapa privado de fuentes',
            description:
              'Registra bases, archivos, streams y servicios con propiedad y contexto de conexion en un solo lugar.'
          },
          {
            title: 'Flujos policy-first',
            description:
              'Libera movimiento de datos mediante aprobaciones, enmascaramiento, retencion y checks de excepciones antes de ejecutar jobs.'
          },
          {
            title: 'Operaciones observables',
            description:
              'Sigue colas, reintentos, ownership, presion de SLA y estado de ejecucion sin exponer sistemas privados.'
          },
          {
            title: 'Historial de auditoria',
            description:
              'Mantén evidencia buscable para fuentes, decisiones, jobs y salidas asociada a cada flujo.'
          },
          {
            title: 'Entrega controlada',
            description:
              'Envia datos aprobados a BI, exportaciones, notebooks, APIs y apps conservando el historial de entrega.'
          },
          {
            title: 'Footprint predecible',
            description:
              'Empieza contenido, expande flujo por flujo y modela compute con pricing claro de plataforma.'
          }
        ],
        ctaText: 'Quieres ver donde encaja DataSliv en tu stack actual?',
        explorePlatform: 'Explorar plataforma',
        estimateCost: 'Estimar coste'
      },
      calculator: {
        badge: 'Calculadora',
        title: 'Pricing basado en compute units',
        description:
          'Ajusta compute units, storage y volumen mensual de queries para ver el coste de DataSliv y la comparacion cloud en una sola vista. La comparacion cloud sigue la logica del articulo aportado: costes de query mas storage del proveedor.',
        totalMonthlyCost: 'Coste mensual total',
        controls: {
          computeUnits: {
            label: 'Compute units',
            suffix: 'compute units'
          },
          dataVolume: {
            label: 'Volumen de datos',
            suffix: 'TB',
            detailAt: 'a'
          },
          monthlyQueries: {
            label: 'Queries mensuales estimadas',
            suffix: 'queries / mes',
            included: 'Incluido en DataSliv'
          }
        },
        savingsVsCloud: 'Ahorro vs cloud',
        premiumVsCloud: 'Prima vs cloud',
        comparedTo: 'comparado con',
        costBreakdown: 'Desglose de costes',
        customPlanQuestion:
          'Necesitas un plan personalizado para despliegues mayores?',
        getInContact: 'Contactar',
        queryUnit: 'query',
        chartAriaLabel: 'Grafico de barras de coste mensual',
        chartShortLabels: {
          'Platform fee': 'Plataforma',
          'Compute Units Cost': 'Compute',
          'Storage Cost': 'Storage',
          'Microsoft Fabric': 'Fabric'
        },
        breakdownRows: {
          platformFee: 'Fee de plataforma',
          computeUnitsCost: 'Coste de compute units',
          storageCost: 'Coste de storage',
          monthlyPlatformTotal: 'Total mensual de plataforma'
        },
        chart: {
          dataSlivCostGraph: 'Grafico de coste DataSliv',
          cloudComparisonGraph: 'Comparacion de costes',
          breakdownDescription:
            'Fee de plataforma, coste de compute units y coste de storage.',
          comparisonDescriptionPrefix:
            'Segun la logica del articulo: las estimaciones cloud aplican costes por query a',
          comparisonDescriptionSuffix:
            'queries mensuales y despues suman storage del proveedor por TB.',
          threeValues: '3 valores',
          queriesPlusStorage: 'Queries + storage',
          compareCloud: 'Comparar costes',
          dataSlivGraph: 'Grafico DataSliv'
        },
        source: {
          basedOnArticle: 'Basado en el articulo:',
          by: 'por',
          benchmarkUses: 'El benchmark usa',
          workload: 'benchmark coffee shop de 3,18 mil millones de filas',
          reportedCostPrefix: 'el coste total reportado para las',
          reportedCostSuffix:
            'queries se usa como entrada de tarifa por query y despues se agrega storage del proveedor por TB.'
        },
        customComparison: {
          title: 'Comparacion personalizada',
          description:
            'Quieres que comparemos una configuracion real de Fabric, Databricks o Snowflake contra DataSliv?'
        },
        computeUnitModel: 'Modelo de compute units',
        computeUnitServices: [
          'Worker Spark',
          'Broker Kafka',
          'Worker Trino',
          'Worker Airflow'
        ],
        unitsAbbreviation: 'CU'
      },
      compliance: {
        eyebrow: 'CONFIANZA Y CUMPLIMIENTO',
        title: 'La confianza como base.',
        description:
          'Diseñado teniendo en cuenta la seguridad, la privacidad y los requisitos normativos.',
        listLabel: 'Normas y marcos de referencia relevantes',
        note: 'Normas y marcos de referencia relevantes; no constituyen una declaración de certificación o cumplimiento.',
        items: {
          gdpr: 'Protección de datos',
          soc2: 'Controles de confianza',
          iso27001: 'Seguridad de la información',
          hipaa: 'Privacidad de datos de salud',
          ccpa: 'Privacidad del consumidor'
        }
      },
      security: {
        badge: 'Operaciones on-prem',
        title: 'Listo para probar DataSliv?',
        bookDemo: 'Reservar demo',
        infrastructureCards: [
          {
            title: 'Rutas privadas de datos',
            description:
              'Conecta fuentes, jobs y capas de reporting a traves de redes internas.'
          },
          {
            title: 'Ejecucion controlada',
            description:
              'Ejecuta flujos gobernados con aprobaciones, ownership y estado operativo claro.'
          },
          {
            title: 'Actividad lista para auditoria',
            description:
              'Sigue quien cambio que, cuando avanzaron los jobs y donde ocurrieron excepciones.'
          },
          {
            title: 'Controles de politicas',
            description:
              'Mantén explicitas las reglas de flujo, rutas de acceso y condiciones de release.'
          },
          {
            title: 'Visibilidad operativa',
            description:
              'Da a los equipos un solo lugar para ver movimiento, fallos, aprobaciones y dependencias.'
          }
        ]
      },
      contactSection: {
        title:
          'Lleva una capa moderna de flujos de datos a tu entorno on-prem.',
        description:
          'Comparte el flujo que quieres gobernar y te responderemos con una ruta practica para tu entorno.',
        signal:
          'El discovery empieza con tu movimiento de datos actual, aprobaciones y cuellos de botella de reporting.',
        getInContact: 'Contactar'
      }
    },
    insights: {
      heroBadge: 'Insights',
      title: 'Ideas practicas para plataformas de datos privadas.',
      description:
        'Textos breves sobre coste, control y arquitectura on-prem para equipos que quieren menos sorpresas en produccion.',
      latestLabel: 'Ultimas publicaciones',
      loadingPosts: 'Cargando publicaciones…',
      emptyFeedTitle: 'Actualmente no hay blogs ni noticias.',
      emptyFeedDescription:
        'Las nuevas publicaciones aparecerán aquí cuando estén disponibles.',
      linksLabel: 'Enlaces',
      allInsights: 'Todos los insights',
      quickLinks: [
        {
          label: 'Blogs',
          to: '/insights#latest-insights'
        },
        {
          label: 'Casos',
          to: '/cases'
        },
        {
          label: 'Contacto',
          to: '/contact'
        }
      ],
      filters: {
        title: 'Filtrar publicaciones',
        description:
          'Muestra blogs o noticias por tipo de contenido, servicios e industrias.',
        typeLabel: 'Tipo de contenido',
        serviceLabel: 'Servicios',
        industryLabel: 'Industrias',
        allTypes: 'Todas las publicaciones',
        allServices: 'Todos los servicios',
        allIndustries: 'Todas las industrias',
        reset: 'Resetear filtros',
        results: 'resultados',
        emptyTitle: 'No hay publicaciones para estos filtros.',
        emptyDescription:
          'Prueba otro tipo de contenido, servicio o industria.',
        previous: 'Anterior',
        next: 'Siguiente',
        page: 'Pagina',
        of: 'de',
        typeOptions: [
          {
            label: 'Blog',
            value: 'blog'
          },
          {
            label: 'Noticia',
            value: 'news'
          }
        ],
        serviceOptions: [
          {
            label: 'Plataforma',
            value: 'platform'
          },
          {
            label: 'Calculadora de coste',
            value: 'cost-calculator'
          },
          {
            label: 'Governance',
            value: 'governance'
          },
          {
            label: 'AI y analitica',
            value: 'ai-analytics'
          },
          {
            label: 'Conectores',
            value: 'connectors'
          },
          {
            label: 'Security',
            value: 'security'
          }
        ],
        industryOptions: [
          {
            label: 'Enterprise',
            value: 'enterprise'
          },
          {
            label: 'Sector publico',
            value: 'public-sector'
          },
          {
            label: 'Telecom',
            value: 'telecom'
          },
          {
            label: 'Finanzas',
            value: 'finance'
          },
          {
            label: 'Transporte',
            value: 'transport'
          },
          {
            label: 'Retail',
            value: 'retail'
          }
        ]
      },
      finalCta: {
        title: 'Quieres un insight para tu data stack actual?',
        description:
          'Cuentanos como ingieres, procesas y gobiernas datos hoy, y mapearemos donde se pueden simplificar coste y operaciones.',
        contact: 'Hablar con el equipo'
      }
    },
    blog: {
      backToInsights: 'Volver a insights',
      breadcrumbHome: 'Inicio',
      breadcrumbInsights: 'Insights',
      published: 'Publicado',
      updated: 'Actualizado',
      author: 'Autor',
      readTime: 'Tiempo de lectura',
      service: 'Servicio',
      industry: 'Industria',
      relatedTitle: 'Publicaciones relacionadas',
      relatedDescription:
        'Mas textos de servicios similares y contextos industriales cercanos.',
      readArticle: 'Leer publicacion',
      articleType: 'Tipo',
      loadingArticle: 'Cargando publicación…',
      loadErrorTitle: 'La publicación no está disponible',
      loadErrorDescription:
        'No se pudo conectar con Blogger. Inténtalo de nuevo en unos momentos.',
      authorRoleFallback: 'Autor de DataSliv',
      tags: 'Temas',
      share: 'Compartir',
      shareOnLinkedIn: 'Compartir en LinkedIn',
      shareOnX: 'Compartir en X',
      shareByEmail: 'Compartir por email',
      copyLink: 'Copiar enlace',
      linkCopied: 'Enlace copiado',
      continueReading: 'Seguir leyendo',
      browseAllInsights: 'Ver todos los insights',
      articleImage: 'Imagen del artículo'
    },
    cases: {
      heroBadge: 'Casos',
      title: 'Casos DataSliv donde coste y control deciden.',
      description:
        'Tenemos cuatro casos relevantes de DataSliv: dos referencias publicas que podemos nombrar y dos entornos on-prem confidenciales que mostramos por el problema resuelto. El hilo comun es claro: mucho menor coste, soberania de datos y una plataforma basada en principios open source.',
      estimateCost: 'Estimar coste',
      explorePlatform: 'Explorar plataforma',
      heroSelectorPrefix: 'DataSliv para',
      heroCategoryAriaLabel: 'Elegir el foco de la pagina de casos',
      heroCategories: [
        'Optimizacion de coste',
        'Plataformas privadas',
        'Data engineering',
        'Governance',
        'Infraestructura AI-ready'
      ],
      heroPrimaryCta: 'Explorar casos',
      calculateSavings: 'Calcular ahorro',
      bookDemo: 'Reservar demo',
      platformName: 'Plataforma DataSliv',
      heroVisual: {
        sourceLabel: 'Fuentes de datos',
        outcomeLabel: 'Resultados',
        sources: ['CRM', 'Bases', 'Archivos', 'Eventos', 'APIs'],
        platformSteps: ['Ingest', 'Process', 'Govern', 'Monitor'],
        outcomes: ['Menor coste', 'Infraestructura privada', 'Datos AI-ready']
      },
      outcomeOverview: {
        title: 'Por que los equipos eligen DataSliv',
        cards: [
          {
            value: 'Coste',
            title: 'Coste de plataforma predecible',
            description:
              'Sustituye servicios dispersos y facturacion variable por un modelo operativo claro.'
          },
          {
            value: 'Control',
            title: 'Infraestructura bajo tu control',
            description:
              'Mantén runtime, conectores, almacenamiento, procesamiento y politicas dentro de tu entorno.'
          },
          {
            value: 'Escala',
            title: 'Un camino mas simple para escalar',
            description:
              'Anade nuevas fuentes y workloads sin reconstruir la plataforma alrededor de cada herramienta.'
          }
        ]
      },
      featured: {
        selectorLabel:
          'Para el mismo flujo de negocio, una plataforma privada consolidada muestra cuanto puede reducirse el coste de plataforma.',
        selectorAriaLabel: 'Elegir caso DataSliv',
        eyebrow: 'Prueba de ahorro',
        proofPoint:
          'Coste 18x menor frente al enfoque anterior de plataforma de datos',
        cta: 'Calcular ahorro potencial'
      },
      workflowTitle: 'Dos referencias publicas, dos casos anonimizados',
      workflowDescription:
        'Inspira y Cropt pueden mostrarse por nombre. Los otros dos casos se presentan como telecom y sector judicial, porque lo importante es por que se eligio DataSliv: operacion on-prem, soberania de datos y base open-core.',
      workflowSteps: [
        'Inspira Grupa - referencia publica',
        'Cropt - referencia publica',
        'Entorno telecom - anonimizado',
        'Entorno judicial - anonimizado'
      ],
      sectionBadge: 'DataSliv en practica',
      sectionTitle: 'Referencias publicas y ejemplos confidenciales.',
      patternBadge: 'Por que los equipos eligieron DataSliv',
      patternTitle: 'Menor coste sin renunciar al control.',
      patternDescription:
        'Los casos no son solo implementaciones tecnicas. En la practica, la decision se reduce a que la plataforma debe ser mas barata que apilar servicios, debe permanecer bajo control del cliente y debe operar en un entorno privado.',
      capabilityBadge: 'Capacidades de plataforma',
      capabilities: [
        {
          eyebrow: 'Operaciones predecibles',
          title:
            'Ejecuta workflows criticos de datos sobre un modelo predecible',
          description:
            'Mueve ingestion y procesamiento fuera de servicios dispersos de pago por uso. Planifica capacidad, fuentes y workloads en una plataforma privada con costes operativos mas claros.',
          cta: 'Calcular ahorro potencial',
          metrics: ['Plan de jobs', 'Estado runtime', 'Forecast de coste']
        },
        {
          eyebrow: 'Consolidacion',
          title: 'Sustituye un stack fragmentado por una plataforma',
          description:
            'Lleva conectores, procesamiento, orquestacion, governance y monitorizacion a una capa operativa en vez de mantener servicios separados para cada workflow.',
          cta: 'Ver como funciona',
          metrics: ['Un core', 'Menos handoffs', 'Mas reutilizacion']
        },
        {
          eyebrow: 'Control privado',
          title: 'Mantén datos, politicas y runtime bajo tu control',
          description:
            'Despliega DataSliv dentro de tu infraestructura y conserva ownership sobre almacenamiento, politicas de acceso, procesamiento y decisiones operativas.',
          cta: 'Ver arquitectura',
          metrics: ['Policy layer', 'Role access', 'Eventos audit']
        },
        {
          eyebrow: 'Base AI-ready',
          title: 'Construye una base AI-ready sin duplicar la plataforma',
          description:
            'Usa datos gobernados y procesados para analitica, machine learning y operaciones de AI sin enviar cada workload por otro servicio desconectado.',
          cta: 'Explorar plataforma',
          metrics: ['Lineage', 'Quality checks', 'Trusted features']
        },
        {
          eyebrow: 'Escalado',
          title: 'Anade fuentes y workloads sin sorpresas de facturacion',
          description:
            'Conecta nuevos sistemas, incrementa volumen y amplia procesamiento mientras arquitectura y costes operativos siguen siendo faciles de entender.',
          cta: 'Hablar con el equipo',
          metrics: ['Fuentes crecientes', 'Core estable', 'Capacidad clara']
        }
      ],
      carousel: {
        title: 'Que ganan los equipos con DataSliv',
        description:
          'Cuando no hay una cita verificada de cliente, mostramos resultados operativos comprobables en lugar de declaraciones inventadas.',
        previous: 'Resultado anterior',
        next: 'Resultado siguiente',
        selectorAriaLabel: 'Elegir resultado',
        cards: [
          {
            title: 'Finanzas obtiene planificacion de coste predecible',
            description:
              'Los equipos pueden comparar workloads reales con un modelo privado en vez de perseguir facturas cambiantes en varios servicios.'
          },
          {
            title: 'Engineering mantiene control del runtime',
            description:
              'Conectores, procesamiento, politicas y estado operativo permanecen cerca de la infraestructura que el equipo ya conoce.'
          },
          {
            title: 'Las nuevas fuentes son mas faciles de anadir',
            description:
              'El patron de plataforma se reutiliza para la siguiente fuente, pipeline o equipo, en vez de convertir cada solicitud en un proyecto separado.'
          },
          {
            title: 'Governance sigue siendo parte de la plataforma',
            description:
              'Checks de politica, limites de rol y eventos de auditoria acompanan el flujo desde ingest hasta entrega.'
          },
          {
            title: 'Los workloads de AI usan una base trusted data',
            description:
              'Datasets procesados, gobernados y trazables pueden soportar AI sin un stack de datos paralelo.'
          }
        ]
      },
      finalCta: {
        title: 'Mira cuanto te cuesta realmente tu plataforma de datos actual.',
        description:
          'Compara servicios cloud dispersos con una plataforma privada predecible construida alrededor de tus workloads reales.'
      },
      caseCards: [
        {
          eyebrow: 'Referencia publica',
          title: 'Inspira Grupa',
          summary:
            'DataSliv sustituyo un enfoque de plataforma de datos mas caro por una solucion privada y consolidada. Para el mismo flujo de negocio, Inspira obtuvo un coste 18x menor frente al modelo anterior, con un marco mensual mas claro y mas control sobre los datos.',
          outcomes: [
            'Coste de plataforma 18x menor',
            'Modelo mensual predecible',
            'Control tecnico retenido'
          ]
        },
        {
          eyebrow: 'Referencia publica',
          title: 'Cropt',
          summary:
            'Cropt usa DataSliv como una forma mas economica de unificar ingestion, procesamiento y analitica sin acumular servicios costosos. El foco esta en pasar antes de la fuente al insight util, con el coste bajo control.',
          outcomes: [
            'Menor coste de plataforma',
            'Flujos consolidados',
            'Analitica lista para crecer'
          ]
        },
        {
          eyebrow: 'Caso confidencial',
          title: 'Entorno telecom con requisitos estrictos',
          summary:
            'Para un entorno telecom con requisitos altos de soberania de datos, DataSliv se despliega on-prem y se apoya en una arquitectura open-core basada en componentes open source. Los datos, el runtime y las operaciones permanecen bajo control del cliente.',
          outcomes: [
            'Soberania de datos',
            'Despliegue on-prem',
            'Base open-core'
          ]
        },
        {
          eyebrow: 'Caso confidencial',
          title: 'Entorno judicial',
          summary:
            'En un entorno judicial, la prioridad es que los datos sensibles no salgan de la infraestructura aprobada. DataSliv permite flujos privados, trazabilidad de auditoria y una solucion basada en principios open source, sin depender de un stack cloud cerrado.',
          outcomes: [
            'Flujos privados',
            'Auditoria y control',
            'Principio open source'
          ]
        }
      ],
      operatingPatterns: [
        {
          title: 'Una plataforma en vez de apilar servicios caros',
          description:
            'DataSliv consolida flujos de datos en un modelo predecible, asi que el cost-effectiveness se vuelve el mensaje principal en lugar de otra licencia, integracion o factura cloud.'
        },
        {
          title: 'Soberania de datos en entorno privado',
          description:
            'Para organizaciones que no pueden perder control sobre datos, conectores y runtime, DataSliv permanece on-prem y opera dentro de la infraestructura aprobada.'
        },
        {
          title: 'Base open-core sin lock-in cerrado',
          description:
            'La plataforma se basa en principios open source, con una capa de produccion para soporte, control y expansion sin depender por completo de un stack cloud cerrado.'
        }
      ]
    },
    contact: {
      badge: 'Contacto',
      title: 'Cuéntanos que necesita manejar tu flujo de datos.',
      description:
        'Envia una nota breve sobre tu entorno, el movimiento de datos que necesitas controlar o el equipo que quieres apoyar. Te responderemos por email.',
      supportPoints: [
        'Discovery de flujos privados de datos',
        'Preguntas sobre despliegue on-prem',
        'Requisitos de gobernanza y auditoria'
      ],
      explorePlatform: 'Explorar plataforma',
      formTitle: 'Contactar',
      mailerWarningBeforeCode: 'Añade',
      mailerWarningAfterCode: 'para habilitar envios reales del formulario.',
      labels: {
        company: 'Empresa',
        firstName: 'Nombre *',
        lastName: 'Apellido *',
        email: 'Email *',
        message: 'Mensaje *'
      },
      sendMessage: 'Enviar mensaje',
      validation: {
        firstNameMin: 'El nombre debe tener al menos 2 caracteres.',
        firstNameMax: 'El nombre debe tener 80 caracteres o menos.',
        firstNamePattern:
          'El nombre solo puede contener letras, espacios, guiones o apostrofes.',
        lastNameMin: 'El apellido debe tener al menos 2 caracteres.',
        lastNameMax: 'El apellido debe tener 80 caracteres o menos.',
        lastNamePattern:
          'El apellido solo puede contener letras, espacios, guiones o apostrofes.',
        emailInvalid: 'Introduce una direccion de email valida.',
        emailMax: 'El email debe tener 254 caracteres o menos.',
        messageMin: 'El mensaje debe tener al menos 5 caracteres.',
        messageMax: 'El mensaje debe tener 2000 caracteres o menos.'
      },
      notifications: {
        received: 'Gracias, tu mensaje ha sido recibido.',
        mailerMissing:
          'El mailer de contacto no esta configurado. Añade VITE_GOOGLE_MAILER_URL para habilitar envios.',
        sent: 'Gracias, tu mensaje ha sido enviado.',
        failed: 'Algo salio mal al enviar tu mensaje. Intentalo de nuevo.'
      }
    },
    notFound: {
      routeSignal: 'Señal de ruta',
      live: 'activa',
      connectionLost: 'Conexion perdida',
      checkingRoute: 'Comprobando ruta',
      pageBadge: 'Pagina 404',
      routeCheckBadge: 'Comprobacion de ruta',
      title: 'Ruta desconectada.',
      description:
        'Esta direccion no forma parte del mapa actual de DataSliv. La ruta se resuelve por un momento y despues el hilo de señal pasa a estado 404.',
      goHome: 'Ir al inicio',
      goBack: 'Volver',
      requestedRoute: 'Ruta solicitada',
      explorePlatformTitle: 'Explorar la plataforma',
      explorePlatformDescription: 'Empieza desde la vista general de DataSliv.',
      viewCasesTitle: 'Ver casos',
      viewCasesDescription: 'Mira flujos de datos on-prem habituales.',
      contactTeamTitle: 'Contactar al equipo',
      contactTeamDescription: 'Cuéntanos que estabas intentando encontrar.'
    },
    errorBoundary: {
      title: 'Ups! Algo salio mal',
      description:
        'Encontramos un error inesperado. No te preocupes, no es culpa tuya.',
      detailsSummary: 'Detalles del error (modo dev)',
      tryAgain: 'Intentar de nuevo',
      reloadPage: 'Recargar pagina',
      goHome: 'Ir al inicio'
    }
  },
  en: {
    meta: {
      title: 'DataSliv - On-prem data solution',
      description:
        'DataSliv is an on-prem data solution for private ingestion, governance, workflow automation, and reporting inside your own infrastructure.',
      keywords:
        'DataSliv, on-prem data, private data platform, data governance, workflow automation, data operations',
      ogDescription:
        'A private control plane for data ingestion, governance, automation, and reporting inside your own infrastructure.',
      imageAlt: 'DataSliv logo',
      twitterDescription: 'A private control plane for on-prem data workflows.'
    },
    common: {
      backToTop: 'Back to top',
      close: 'Close',
      dataSliv: 'DataSliv',
      soc2Compliant: 'SOC 2 Compliant',
      month: 'month',
      year: 'year',
      total: 'Total'
    },
    nav: {
      logoAlt: 'DataSliv logo',
      primaryAriaLabel: 'Primary navigation',
      openMenu: 'Open menu',
      closeMenu: 'Close menu',
      platform: 'Platform',
      calculator: 'Calculator',
      cases: 'Cases',
      insights: 'Insights',
      insightsDropdownAriaLabel: 'Insights menu',
      contact: 'Contact',
      demoShort: 'Demo',
      demoLong: 'Get a demo'
    },
    footer: {
      tagline: 'DataSliv - on-prem data operations'
    },
    language: {
      names: {
        sr: 'Serbian',
        es: 'Spanish',
        en: 'English'
      },
      selectorLabel: 'Select language'
    },
    theme: {
      light: 'Light',
      dark: 'Dark',
      switchToLightMode: 'Switch to light mode',
      switchToDarkMode: 'Switch to dark mode'
    },
    home: {
      hero: {
        badge: 'Cost-effective private data platform',
        title: 'Enterprise Data Infrastructure Without Enterprise Cloud Bills.',
        descriptionBeforeAccent:
          'DataSliv replaces rising cloud bills and scattered tools with one ',
        descriptionAccent: 'predictable',
        descriptionAfterAccent:
          ' layer for ingestion, processing, governance, and AI inside your infrastructure.',
        startMapping: 'Explore platform',
        getDemo: 'Get a demo',
        lifecycleStages: {
          ingest: 'Ingest',
          govern: 'Govern',
          operate: 'Operate',
          monitor: 'Monitor'
        }
      },
      technology: {
        benefitsTitle: 'Platform benefits at a glance',
        benefitsAriaLabel: 'Platform benefits',
        technologies: [
          'Up to 90% Cost Reduction',
          'Zero Cloud Lock-in',
          'High Availability',
          'Automated Backups',
          'End-to-End Encryption',
          'From 30-Minute Setup',
          'Bank-Grade Security',
          'SOC 2 Compliant',
          'AI-Ready Platform',
          'Compliance Made Easy',
          'Zero DevOps Required',
          'Enterprise-Grade'
        ],
        environmentsTitle: 'Built for data-sensitive company environments',
        environments: [
          'Financial services',
          'Telecom operators',
          'Manufacturing',
          'Healthcare groups',
          'Public sector',
          'Energy'
        ]
      },
      platform: {
        badge: 'Cost-effective platform',
        title:
          'Everything the data stack needs, without stacking costly services.',
        description:
          'DataSliv brings ingestion, processing, storage, analytics, warehouse, MLOps, and AI into a private environment, so you pay for one predictable platform instead of multiple cloud bills, licenses, and integrations. Non-technical teams see clear cost and outcomes; technical teams keep control over runtime, connectors, lineage, and policies.',
        menuLabel: 'Platform menu',
        navAriaLabel: 'Platform capabilities',
        scrollToPrefix: 'Scroll to',
        layerLabel: 'Platform layer',
        privateRuntime: 'Private runtime',
        typicalPath: 'Typical path',
        metrics: {
          layer: 'Layer',
          controls: 'Controls',
          pathStages: 'Path stages'
        },
        stepPrefix: 'Step',
        capabilities: [
          {
            label: 'Ingestion',
            title: 'Data ingestion',
            lead: 'Bring every approved source into one controlled intake layer.',
            description:
              'Connect databases, APIs, files, streams, and operational systems without moving ownership away from your environment. Each source is registered with credentials, schema context, sync rules, and health checks before data starts flowing.',
            outcomes: [
              'Batch, streaming, and API intake',
              'Source contracts and ownership',
              'Connection monitoring and retries'
            ],
            route: ['Sources', 'Contracts', 'Validation', 'Landing zone']
          },
          {
            label: 'Processing',
            title: 'Data processing',
            lead: 'Turn raw movement into repeatable, observable transformation jobs.',
            description:
              'Clean, enrich, join, and prepare data through scheduled or event-driven workflows. Teams can track job status, failed runs, retries, lineage, and SLA pressure without digging through separate scripts and logs.',
            outcomes: [
              'Orchestration for pipelines and tasks',
              'Reusable transformation patterns',
              'Run history, lineage, and alerts'
            ],
            route: ['Validate', 'Transform', 'Enrich', 'Publish']
          },
          {
            label: 'Data lake',
            title: 'Data lake',
            lead: 'Store raw and curated data in a private, scalable lake foundation.',
            description:
              'Keep high-volume structured, semi-structured, and unstructured data close to your infrastructure. DataSliv organizes landing, bronze, silver, and gold zones with access control, retention rules, and cost-aware storage planning.',
            outcomes: [
              'Raw and curated storage zones',
              'Schema evolution and retention policy',
              'Private cloud or on-prem deployment'
            ],
            route: ['Raw zone', 'Curated zone', 'Catalog', 'Governance']
          },
          {
            label: 'Analytics',
            title: 'Data Analytics',
            lead: 'Give teams trusted datasets for reporting, exploration, and decisions.',
            description:
              'Expose governed data to BI dashboards, notebooks, operational reports, and self-service analysis. Analysts get documented datasets while platform teams keep control of permissions, lineage, and refresh quality.',
            outcomes: [
              'BI-ready datasets and semantic views',
              'Notebook and SQL exploration',
              'Metric definitions and refresh checks'
            ],
            route: ['Catalog', 'Metrics', 'Dashboards', 'Insights']
          },
          {
            label: 'Warehouse',
            title: 'Data warehouse',
            lead: 'Serve business-critical reporting from modeled, reliable data marts.',
            description:
              'Model curated data into warehouse tables, dimensional layers, and domain-specific marts for finance, operations, product, and leadership reporting. Compute and storage stay predictable because warehouse workloads are separated from raw lake activity.',
            outcomes: [
              'Dimensional models and marts',
              'Workload-aware compute planning',
              'Trusted reporting tables'
            ],
            route: ['Model', 'Optimize', 'Serve', 'Audit']
          },
          {
            label: 'MLOps',
            title: 'MLOps',
            lead: 'Move models from experiments to governed, repeatable operations.',
            description:
              'Coordinate feature preparation, training jobs, model registry workflows, approvals, deployments, and monitoring. Data scientists can iterate while operations teams keep reproducibility, rollback paths, and compliance evidence visible.',
            outcomes: [
              'Feature and training pipelines',
              'Model registry and release checks',
              'Monitoring for drift and quality'
            ],
            route: ['Features', 'Train', 'Register', 'Monitor']
          },
          {
            label: 'AI integration',
            title: 'LLM & AI integration',
            lead: 'Connect private data to AI workflows without losing control.',
            description:
              'Prepare governed datasets, vector indexes, retrieval workflows, and AI application hooks for internal assistants and automation. Sensitive context stays inside approved boundaries while teams get the benefits of modern LLM and AI interfaces.',
            outcomes: [
              'Private retrieval and vector search',
              'Governed AI application context',
              'Human review and audit trails'
            ],
            route: ['Context', 'Retrieval', 'Guardrails', 'AI apps']
          }
        ]
      },
      inspira: {
        badge: 'Proven savings',
        company: 'Inspira Grupa',
        title: 'Inspira Grupa reduced data platform cost by 18x.',
        description:
          'Instead of letting costs keep spreading across multiple services, DataSliv consolidated key data workflows into a private platform with a clear monthly model. The result speaks to both finance and technical teams: the same business workflows, far lower cost, and more control.',
        savingsLabel: 'Achieved savings',
        savingsValue: '18x',
        savingsDetail:
          'lower cost compared with the previous data platform approach',
        proofPoints: [
          {
            title: 'Clear business impact',
            description:
              'Platform cost becomes an ROI conversation, not only an infrastructure conversation.'
          },
          {
            title: 'Technical control stays',
            description:
              'Runtime, connectors, processing, storage, and policies remain under team control.'
          },
          {
            title: 'Easier growth planning',
            description:
              'A predictable model helps new sources and higher volumes get planned without billing surprises.'
          }
        ],
        cta: 'Estimate similar savings'
      },
      buildVsBuy: {
        badge: 'Build vs buy',
        title: 'The hidden cost behind a simple data workflow.',
        description:
          'A script can move data in an afternoon. Building the governed, observable, on-prem operating layer around it is what turns into quarters of engineering work.',
        buildEyebrow: 'Build in-house',
        buildTitle: 'What teams discover too late',
        buyEyebrow: 'DataSliv',
        buyTitle: 'Operating layer included',
        recommended: 'Recommended',
        stats: [
          {
            value: '4',
            label: 'operating layers',
            detail: 'source mapping, policy, operations, and delivery checks'
          },
          {
            value: '30 min',
            label: 'to a contained start',
            detail: 'map the first workflow before a large migration plan'
          },
          {
            value: '0',
            label: 'cloud lock-in required',
            detail: 'keep the runtime on-prem or inside a private cloud'
          }
        ],
        buildCosts: [
          {
            title: 'Source discovery',
            description:
              'A spreadsheet of databases, owners, credentials, and schemas goes stale as soon as teams change.'
          },
          {
            title: 'Policy enforcement',
            description:
              'Masking, retention, approvals, and exceptions have to be rebuilt around every new data route.'
          },
          {
            title: 'Operational handoffs',
            description:
              'Retries, queues, assignments, and SLA pressure become custom platform work instead of data work.'
          },
          {
            title: 'Audit readiness',
            description:
              'Run history, approvals, and output evidence end up scattered across logs, tickets, and chats.'
          },
          {
            title: 'Maintenance',
            description:
              'Every policy change, schema drift, and downstream request becomes another engineering ticket.'
          },
          {
            title: 'Predictable cost',
            description:
              'Infrastructure, code review, support, and dispute handling keep growing outside one operating model.'
          }
        ],
        buyAdvantages: [
          {
            title: 'Private source map',
            description:
              'Register databases, files, streams, and services with ownership and connection context in one place.'
          },
          {
            title: 'Policy-first workflows',
            description:
              'Release data movement through approval, masking, retention, and exception checks before jobs run.'
          },
          {
            title: 'Observable operations',
            description:
              'Track queues, retries, ownership, SLA pressure, and run status without exposing private systems.'
          },
          {
            title: 'Audit history',
            description:
              'Keep searchable evidence for sources, decisions, jobs, and outputs attached to every workflow.'
          },
          {
            title: 'Controlled delivery',
            description:
              'Send approved data to BI, exports, notebooks, APIs, and apps with delivery history preserved.'
          },
          {
            title: 'Predictable footprint',
            description:
              'Start contained, expand workflow by workflow, and model compute with clear platform pricing.'
          }
        ],
        ctaText: 'Want to see where DataSliv fits in your current stack?',
        explorePlatform: 'Explore the platform',
        estimateCost: 'Estimate cost'
      },
      calculator: {
        badge: 'Calculator',
        title: 'Compute Unit-Based Pricing',
        description:
          'Adjust compute units, storage, and monthly query volume to see DataSliv cost and cloud comparison in one view. Cloud comparison follows the provided article-based logic: query costs plus provider storage.',
        totalMonthlyCost: 'Total Monthly Cost',
        controls: {
          computeUnits: {
            label: 'Compute Units',
            suffix: 'compute units'
          },
          dataVolume: {
            label: 'Data Volume',
            suffix: 'TB',
            detailAt: 'at'
          },
          monthlyQueries: {
            label: 'Estimated Monthly Queries',
            suffix: 'queries / month',
            included: 'Included in DataSliv'
          }
        },
        savingsVsCloud: 'Savings vs cloud',
        premiumVsCloud: 'Premium vs cloud',
        comparedTo: 'compared to',
        costBreakdown: 'Cost breakdown',
        customPlanQuestion: 'Need a custom plan for larger deployments?',
        getInContact: 'Get in contact',
        queryUnit: 'query',
        chartAriaLabel: 'Monthly cost bar chart',
        chartShortLabels: {
          'Platform fee': 'Platform',
          'Compute Units Cost': 'Compute',
          'Storage Cost': 'Storage',
          'Microsoft Fabric': 'Fabric'
        },
        breakdownRows: {
          platformFee: 'Platform fee',
          computeUnitsCost: 'Compute Units Cost',
          storageCost: 'Storage Cost',
          monthlyPlatformTotal: 'Monthly platform total'
        },
        chart: {
          dataSlivCostGraph: 'DataSliv cost graph',
          cloudComparisonGraph: 'Cost comparison',
          breakdownDescription:
            'Platform fee, compute units cost, and storage cost.',
          comparisonDescriptionPrefix:
            'Based on article logic: cloud estimates apply per-query costs to',
          comparisonDescriptionSuffix:
            'monthly queries, then add provider storage per TB.',
          threeValues: '3 values',
          queriesPlusStorage: 'Queries + storage',
          compareCloud: 'Compare costs',
          dataSlivGraph: 'DataSliv graph'
        },
        source: {
          basedOnArticle: 'Based on article:',
          by: 'by',
          benchmarkUses: 'The benchmark uses',
          workload: '3.18B-row coffee shop benchmark',
          reportedCostPrefix: 'reported total cost for the',
          reportedCostSuffix:
            'queries is used as the query-rate input, then provider storage is added per TB.'
        },
        customComparison: {
          title: 'Custom comparison',
          description:
            'Want us to compare a real Fabric, Databricks, or Snowflake setup against DataSliv?'
        },
        computeUnitModel: 'Compute unit model',
        computeUnitServices: [
          'Spark worker',
          'Kafka broker',
          'Trino worker',
          'Airflow worker'
        ],
        unitsAbbreviation: 'CU'
      },
      compliance: {
        eyebrow: 'TRUST & COMPLIANCE',
        title: 'Built with trust at its core.',
        description:
          'Designed with security, privacy, and regulatory requirements in mind.',
        listLabel: 'Relevant standards and frameworks',
        note: 'Relevant standards and frameworks; not a claim of certification or compliance.',
        items: {
          gdpr: 'Data protection',
          soc2: 'Trust services criteria',
          iso27001: 'Information security',
          hipaa: 'Health data privacy',
          ccpa: 'Consumer privacy'
        }
      },
      security: {
        badge: 'On-prem operations',
        title: 'Ready to try DataSliv?',
        bookDemo: 'Book a demo',
        infrastructureCards: [
          {
            title: 'Private data paths',
            description:
              'Connect sources, jobs, and reporting layers across internal networks.'
          },
          {
            title: 'Controlled execution',
            description:
              'Run governed workflows with approvals, ownership, and clear operational status.'
          },
          {
            title: 'Audit-ready activity',
            description:
              'Track who changed what, when jobs moved, and where exceptions happened.'
          },
          {
            title: 'Policy controls',
            description:
              'Keep workflow rules, access paths, and release conditions explicit.'
          },
          {
            title: 'Operational visibility',
            description:
              'Give teams one place to see movement, failures, approvals, and dependencies.'
          }
        ]
      },
      contactSection: {
        title:
          'Bring a modern data workflow layer to your on-prem environment.',
        description:
          'Share the workflow you want to govern and we will follow up with a practical path for your environment.',
        signal:
          'Discovery starts with your current data movement, approval, and reporting bottlenecks.',
        getInContact: 'Get in contact'
      }
    },
    insights: {
      heroBadge: 'Insights',
      title: 'Practical insight for private data platforms.',
      description:
        'Short reads on cost, control, and on-prem architecture for teams that want fewer surprises in production.',
      latestLabel: 'Latest posts',
      loadingPosts: 'Loading posts…',
      emptyFeedTitle: 'There are currently no blog posts or news.',
      emptyFeedDescription:
        'New posts will appear here as soon as they are available.',
      linksLabel: 'Links',
      allInsights: 'All insights',
      quickLinks: [
        {
          label: 'Blogs',
          to: '/insights#latest-insights'
        },
        {
          label: 'Cases',
          to: '/cases'
        },
        {
          label: 'Contact',
          to: '/contact'
        }
      ],
      filters: {
        title: 'Filter posts',
        description:
          'Show blogs or news by content type, services, and industries.',
        typeLabel: 'Content type',
        serviceLabel: 'Services',
        industryLabel: 'Industries',
        allTypes: 'All posts',
        allServices: 'All services',
        allIndustries: 'All industries',
        reset: 'Reset filters',
        results: 'results',
        emptyTitle: 'No posts match these filters.',
        emptyDescription: 'Try another content type, service, or industry.',
        previous: 'Previous',
        next: 'Next',
        page: 'Page',
        of: 'of',
        typeOptions: [
          {
            label: 'Blog',
            value: 'blog'
          },
          {
            label: 'News',
            value: 'news'
          }
        ],
        serviceOptions: [
          {
            label: 'Platform',
            value: 'platform'
          },
          {
            label: 'Cost calculator',
            value: 'cost-calculator'
          },
          {
            label: 'Governance',
            value: 'governance'
          },
          {
            label: 'AI and analytics',
            value: 'ai-analytics'
          },
          {
            label: 'Connectors',
            value: 'connectors'
          },
          {
            label: 'Security',
            value: 'security'
          }
        ],
        industryOptions: [
          {
            label: 'Enterprise',
            value: 'enterprise'
          },
          {
            label: 'Public sector',
            value: 'public-sector'
          },
          {
            label: 'Telecom',
            value: 'telecom'
          },
          {
            label: 'Finance',
            value: 'finance'
          },
          {
            label: 'Transport',
            value: 'transport'
          },
          {
            label: 'Retail',
            value: 'retail'
          }
        ]
      },
      finalCta: {
        title: 'Want an insight for your current data stack?',
        description:
          'Tell us how you ingest, process, and govern data today, and we will map where cost and operations can be simplified.',
        contact: 'Talk to the team'
      }
    },
    blog: {
      backToInsights: 'Back to insights',
      breadcrumbHome: 'Home',
      breadcrumbInsights: 'Insights',
      published: 'Published',
      updated: 'Updated',
      author: 'Author',
      readTime: 'Read time',
      service: 'Service',
      industry: 'Industry',
      relatedTitle: 'Related posts',
      relatedDescription:
        'More reads from similar services and industry contexts.',
      readArticle: 'Read article',
      articleType: 'Type',
      loadingArticle: 'Loading article…',
      loadErrorTitle: 'This article is currently unavailable',
      loadErrorDescription:
        'The connection to Blogger failed. Please try again in a few moments.',
      authorRoleFallback: 'DataSliv author',
      tags: 'Topics',
      share: 'Share',
      shareOnLinkedIn: 'Share on LinkedIn',
      shareOnX: 'Share on X',
      shareByEmail: 'Share by email',
      copyLink: 'Copy link',
      linkCopied: 'Link copied',
      continueReading: 'Continue reading',
      browseAllInsights: 'Browse all insights',
      articleImage: 'Article image'
    },
    cases: {
      heroBadge: 'Cases',
      title: 'DataSliv cases where cost and control decide.',
      description:
        'We have four relevant DataSliv cases: two public references we can name and two confidential on-prem environments shown through the problems they solved. The common thread is simple: much lower cost, data sovereignty, and a platform built on open source principles.',
      estimateCost: 'Estimate cost',
      explorePlatform: 'Explore platform',
      heroSelectorPrefix: 'DataSliv for',
      heroCategoryAriaLabel: 'Choose the cases page focus',
      heroCategories: [
        'Cost optimization',
        'Private data platforms',
        'Data engineering',
        'Governance',
        'AI-ready infrastructure'
      ],
      heroPrimaryCta: 'Explore the cases',
      calculateSavings: 'Calculate your savings',
      bookDemo: 'Book a demo',
      platformName: 'DataSliv platform',
      heroVisual: {
        sourceLabel: 'Data sources',
        outcomeLabel: 'Outcomes',
        sources: ['CRM', 'Databases', 'Files', 'Events', 'APIs'],
        platformSteps: ['Ingest', 'Process', 'Govern', 'Monitor'],
        outcomes: ['Lower cost', 'Private infrastructure', 'AI-ready data']
      },
      outcomeOverview: {
        title: 'Why teams choose DataSliv',
        cards: [
          {
            value: 'Cost',
            title: 'Predictable platform cost',
            description:
              'Replace scattered services and variable billing with one clear operating model.'
          },
          {
            value: 'Control',
            title: 'Infrastructure under your control',
            description:
              'Keep runtime, connectors, storage, processing, and policies inside your environment.'
          },
          {
            value: 'Scale',
            title: 'A simpler path to scale',
            description:
              'Add new data sources and workloads without rebuilding the platform around each new tool.'
          }
        ]
      },
      featured: {
        selectorLabel:
          'For the same business workflow, a consolidated private platform shows how much platform cost can be reduced.',
        selectorAriaLabel: 'Choose a DataSliv case',
        eyebrow: 'Savings proof',
        proofPoint:
          '18x lower cost compared with the previous data platform approach',
        cta: 'Calculate potential savings'
      },
      workflowTitle: 'Two public references, two anonymized cases',
      workflowDescription:
        'Inspira and Cropt can be shown by name. The other two cases are presented as telecom and justice-sector environments, because the important part is why DataSliv was chosen: on-prem operation, data sovereignty, and an open-core foundation.',
      workflowSteps: [
        'Inspira Grupa - public reference',
        'Cropt - public reference',
        'Telecom environment - anonymized',
        'Justice-sector environment - anonymized'
      ],
      sectionBadge: 'DataSliv in practice',
      sectionTitle: 'Public references and confidential examples.',
      patternBadge: 'Why teams chose DataSliv',
      patternTitle: 'Lower cost without giving up control.',
      patternDescription:
        'The cases are not only technical implementations. In practice, the decision comes down to a platform that must be cheaper than stacking services, remain under the customer’s control, and run in a private environment.',
      capabilityBadge: 'Platform capabilities',
      capabilities: [
        {
          eyebrow: 'Predictable operations',
          title: 'Run critical data workflows on a predictable model',
          description:
            'Move ingestion and processing away from scattered usage-based services. Plan capacity, sources, and workloads through one private platform with clearer operating costs.',
          cta: 'Calculate potential savings',
          metrics: ['Job schedule', 'Runtime status', 'Cost forecast']
        },
        {
          eyebrow: 'Consolidation',
          title: 'Replace a fragmented tool stack with one platform',
          description:
            'Bring connectors, processing, orchestration, governance, and monitoring into one operating layer instead of maintaining separate services for every workflow.',
          cta: 'See how it works',
          metrics: ['One core', 'Fewer handoffs', 'Reusable workflows']
        },
        {
          eyebrow: 'Private control',
          title: 'Keep data, policies and runtime under your control',
          description:
            'Deploy DataSliv inside your infrastructure and retain ownership of storage, access policies, processing, and operational decisions.',
          cta: 'View architecture',
          metrics: ['Policy layer', 'Role access', 'Audit events']
        },
        {
          eyebrow: 'AI-ready foundation',
          title:
            'Build an AI-ready foundation without duplicating your platform',
          description:
            'Use governed and processed data for analytics, machine learning, and AI operations without sending every workload through another disconnected service.',
          cta: 'Explore the platform',
          metrics: ['Lineage', 'Quality checks', 'Trusted features']
        },
        {
          eyebrow: 'Scale',
          title: 'Add sources and workloads without billing surprises',
          description:
            'Connect new systems, increase data volume, and expand processing while keeping architecture and operating costs easier to understand.',
          cta: 'Talk to our team',
          metrics: ['Growing sources', 'Stable core', 'Clear capacity']
        }
      ],
      carousel: {
        title: 'What teams gain with DataSliv',
        description:
          'When verified customer quotes are unavailable, we show concrete outcome cards instead of fabricated testimonials.',
        previous: 'Previous outcome',
        next: 'Next outcome',
        selectorAriaLabel: 'Choose an outcome',
        cards: [
          {
            title: 'Finance gets predictable cost planning',
            description:
              'Teams can compare real workloads against one private model instead of chasing variable bills across multiple services.'
          },
          {
            title: 'Engineering keeps runtime control',
            description:
              'Connectors, processing, policies, and operational status stay close to the infrastructure the team already understands.'
          },
          {
            title: 'New sources become easier to add',
            description:
              'The platform pattern is reused for the next source, pipeline, or team instead of turning every request into a separate project.'
          },
          {
            title: 'Governance remains part of the platform',
            description:
              'Policy checks, role boundaries, and audit events follow the data flow from ingestion through delivery.'
          },
          {
            title: 'AI workloads use trusted data foundations',
            description:
              'Processed, governed, and traceable datasets can support AI work without creating a parallel data stack.'
          }
        ]
      },
      finalCta: {
        title: 'See what your current data platform is really costing you.',
        description:
          'Compare scattered cloud services with a predictable private platform built around your real workloads.'
      },
      caseCards: [
        {
          eyebrow: 'Public reference',
          title: 'Inspira Grupa',
          summary:
            'DataSliv replaced a more expensive data platform approach with a private, consolidated solution. For the same business workflow, Inspira ended up with 18x lower cost than the previous model, with a clearer monthly frame and more control over data.',
          outcomes: [
            '18x lower platform cost',
            'Predictable monthly model',
            'Technical control retained'
          ]
        },
        {
          eyebrow: 'Public reference',
          title: 'Cropt',
          summary:
            'Cropt uses DataSliv as a more cost-effective way to unify ingestion, processing, and analytics without stacking expensive services. The focus is a faster path from source data to useful insight, with platform cost kept under control.',
          outcomes: [
            'Lower platform cost',
            'Consolidated workflows',
            'Analytics ready to scale'
          ]
        },
        {
          eyebrow: 'Confidential case',
          title: 'Telecom environment with strict requirements',
          summary:
            'For a telecom environment with high data sovereignty requirements, DataSliv runs on-prem and relies on an open-core architecture based on open source components. Data, runtime, and operations stay under the customer’s control.',
          outcomes: [
            'Data sovereignty',
            'On-prem deployment',
            'Open-core foundation'
          ]
        },
        {
          eyebrow: 'Confidential case',
          title: 'Justice-sector environment',
          summary:
            'In a justice-sector environment, the priority is keeping sensitive data inside approved infrastructure. DataSliv enables private workflows, audit traceability, and a solution built on open source principles without depending on a closed cloud stack.',
          outcomes: [
            'Private workflows',
            'Audit and control',
            'Open source principle'
          ]
        }
      ],
      operatingPatterns: [
        {
          title: 'One platform instead of stacking costly services',
          description:
            'DataSliv consolidates data workflows into a predictable model, so cost-effectiveness becomes the central message instead of another license, integration, or cloud bill.'
        },
        {
          title: 'Data sovereignty in a private environment',
          description:
            'For organizations that cannot lose control over data, connectors, and runtime, DataSliv stays on-prem and operates inside approved infrastructure.'
        },
        {
          title: 'Open-core foundation without closed lock-in',
          description:
            'The platform is based on open source principles, with a production layer for support, control, and expansion without relying completely on a closed cloud stack.'
        }
      ]
    },
    contact: {
      badge: 'Contact',
      title: 'Tell us what your data workflow needs to handle.',
      description:
        'Send a short note about your environment, the data movement you need to control, or the team you want to support. We will get back to you by email.',
      supportPoints: [
        'Private data workflow discovery',
        'On-prem deployment questions',
        'Governance and audit requirements'
      ],
      explorePlatform: 'Explore platform',
      formTitle: 'Get in contact',
      mailerWarningBeforeCode: 'Add',
      mailerWarningAfterCode: 'to enable live contact submissions.',
      labels: {
        company: 'Company',
        firstName: 'First name *',
        lastName: 'Last name *',
        email: 'Email *',
        message: 'Message *'
      },
      sendMessage: 'Send message',
      validation: {
        firstNameMin: 'First name must be at least 2 characters.',
        firstNameMax: 'First name must be 80 characters or fewer.',
        firstNamePattern:
          'First name can only contain letters, spaces, hyphens, or apostrophes.',
        lastNameMin: 'Last name must be at least 2 characters.',
        lastNameMax: 'Last name must be 80 characters or fewer.',
        lastNamePattern:
          'Last name can only contain letters, spaces, hyphens, or apostrophes.',
        emailInvalid: 'Enter a valid email address.',
        emailMax: 'Email must be 254 characters or fewer.',
        messageMin: 'Message must be at least 5 characters.',
        messageMax: 'Message must be 2000 characters or fewer.'
      },
      notifications: {
        received: 'Thanks, your message has been received.',
        mailerMissing:
          'Contact mailer is not configured. Add VITE_GOOGLE_MAILER_URL to enable submissions.',
        sent: 'Thanks, your message has been sent.',
        failed:
          'Something went wrong while sending your message. Please try again.'
      }
    },
    notFound: {
      routeSignal: 'Route signal',
      live: 'live',
      connectionLost: 'Connection lost',
      checkingRoute: 'Checking route',
      pageBadge: '404 page',
      routeCheckBadge: 'Route check',
      title: 'Route disconnected.',
      description:
        'This address is not part of the current DataSliv map. The route resolves for a moment, then the signal thread snaps into a 404 state.',
      goHome: 'Go home',
      goBack: 'Go back',
      requestedRoute: 'Requested route',
      explorePlatformTitle: 'Explore the platform',
      explorePlatformDescription: 'Start from the DataSliv overview.',
      viewCasesTitle: 'View cases',
      viewCasesDescription: 'See common on-prem data workflows.',
      contactTeamTitle: 'Contact the team',
      contactTeamDescription: 'Tell us what you were trying to find.'
    },
    errorBoundary: {
      title: 'Oops! Something went wrong',
      description:
        "We encountered an unexpected error. Don't worry, it's not your fault.",
      detailsSummary: 'Error Details (Dev Mode)',
      tryAgain: 'Try Again',
      reloadPage: 'Reload Page',
      goHome: 'Go Home'
    }
  }
};

export const isLanguage = (value: unknown): value is Language =>
  typeof value === 'string' && supportedLanguages.includes(value as Language);
